<template>
  <div class="inout-page">
    <!-- 저장 완료 토스트 -->
    <Transition name="toast">
      <div v-if="showSaveToast" class="save-toast">
        <v-icon size="18" color="white">mdi-check-circle</v-icon>
        <span>저장되었습니다</span>
      </div>
    </Transition>
    
    <!-- 로딩 컴포넌트 -->
    <LodingComponent :is-visible="isLoading" text="현금출납부 데이터 로딩 중..." />
    
    <div class="inout-layout" :class="{ 'sidebar-right': sidebarPosition === 'right' }" v-if="!isLoading">
      <!-- 고정 사이드바 (리모컨) -->
      <aside class="inout-sidebar">
        <!-- 위치 토글 버튼 -->
        <button 
          class="sidebar-position-toggle"
          @click="toggleSidebarPosition"
          :title="sidebarPosition === 'left' ? '우측으로 이동' : '좌측으로 이동'"
        >
          <v-icon size="14">{{ sidebarPosition === 'left' ? 'mdi-arrow-right' : 'mdi-arrow-left' }}</v-icon>
        </button>

        <!-- 연도/월 선택 영역 -->
        <div class="sidebar-ym-selector">
          <button 
            class="ym-nav-btn" 
            @click="changeMonth(-1)"
          >
            <v-icon size="18">mdi-chevron-left</v-icon>
          </button>
          <div class="ym-display" @click="openMonthPicker">
            <span class="ym-value">{{ currentYear }}.{{ currentMonth }}</span>
            <input 
              type="month" 
              ref="monthPickerRef"
              :value="`${currentYear}-${currentMonth}`"
              @change="onMonthPickerChange"
              class="hidden-month-picker"
            />
          </div>
          <button 
            class="ym-nav-btn" 
            @click="changeMonth(1)"
          >
            <v-icon size="18">mdi-chevron-right</v-icon>
          </button>
        </div>
        
        <!-- 페이지 타이틀 배지 -->
        <div class="sidebar-title-badge">
          <v-icon size="14">mdi-book-open-page-variant</v-icon>
          <span>현금출납부</span>
        </div>

        <!-- 수입/지출 요약 -->
        <div class="sidebar-summary">
          <!-- 이월금 -->
          <div class="summary-item carryover">
            <div class="summary-header">
              <v-icon size="14" color="#6366f1">mdi-arrow-right-bold</v-icon>
              <span class="label">이월금</span>
            </div>
            <span class="value prev">{{ formatNumber(prevMonthBalance.balance) }}원</span>
          </div>
          <div class="summary-divider"></div>
          <!-- 수입 -->
          <div class="summary-item income">
            <div class="summary-header">
              <span class="dot"></span>
              <span class="label">수입</span>
            </div>
            <span class="value">{{ formatNumber(summaryData.income) }}원</span>
          </div>
          <!-- 지출 -->
          <div class="summary-item expense">
            <div class="summary-header">
              <span class="dot"></span>
              <span class="label">지출</span>
            </div>
            <span class="value">{{ formatNumber(summaryData.expense) }}원</span>
          </div>
          <div class="summary-divider"></div>
          <!-- 누적잔액 = 이월금 + 수입 - 지출 -->
          <div class="summary-item balance" :class="{ warning: totalBalance < 0 }">
            <div class="summary-header">
              <v-icon size="14" :color="totalBalance >= 0 ? '#10b981' : '#ef4444'">
                {{ totalBalance >= 0 ? 'mdi-check-circle' : 'mdi-alert-circle' }}
              </v-icon>
              <span class="label">누적잔액</span>
            </div>
            <span class="value">{{ formatNumber(totalBalance) }}원</span>
          </div>
        </div>

        <!-- 필터 영역 -->
        <div class="sidebar-filter">
          <div class="filter-header">
            <v-icon size="14" color="#64748b">mdi-filter-variant</v-icon>
            <span>구분 필터</span>
          </div>
          <div class="filter-buttons">
            <button 
              class="filter-btn" 
              :class="{ active: filterGubun === 'all' }"
              @click="filterGubun = 'all'"
            >
              전체
            </button>
            <button 
              class="filter-btn income" 
              :class="{ active: filterGubun === 'IN' }"
              @click="filterGubun = 'IN'"
            >
              수입
            </button>
            <button 
              class="filter-btn expense" 
              :class="{ active: filterGubun === 'OUT' }"
              @click="filterGubun = 'OUT'"
            >
              지출
            </button>
          </div>
        </div>
        
        <!-- 개별 검색 필터 -->
        <div class="sidebar-search-filters">
          <div class="search-filter-item">
            <label class="search-label">계정</label>
          <div class="search-input-wrapper">
            <input
              type="text"
              class="search-input"
                v-model="searchScode"
                placeholder="계정코드..."
              />
              <button v-if="searchScode" class="search-clear" @click="searchScode = ''">
                <v-icon size="12">mdi-close</v-icon>
              </button>
            </div>
          </div>
          <div class="search-filter-item">
            <label class="search-label">적요</label>
            <div class="search-input-wrapper">
              <input
                type="text"
                class="search-input"
                v-model="searchMemo"
                placeholder="적요 검색..."
              />
              <button v-if="searchMemo" class="search-clear" @click="searchMemo = ''">
                <v-icon size="12">mdi-close</v-icon>
              </button>
            </div>
          </div>
          <div class="search-filter-item">
            <label class="search-label">금액</label>
            <div class="search-input-wrapper">
              <input
                type="text"
                class="search-input"
                v-model="searchPrice"
                placeholder="금액 검색..."
            />
              <button v-if="searchPrice" class="search-clear" @click="searchPrice = ''">
                <v-icon size="12">mdi-close</v-icon>
              </button>
            </div>
          </div>
            <button 
            v-if="searchScode || searchMemo || searchPrice" 
            class="search-clear-all"
            @click="clearAllSearch"
            >
            <v-icon size="12">mdi-filter-remove</v-icon>
            초기화
            </button>
        </div>

        <!-- 빠른 필터 -->
        <div class="quick-filters">
          <button 
            class="quick-filter-btn sort-btn" 
            :class="{ active: sortKey === 'hi_idx' && sortOrder === 'asc' }"
            @click="setSortToChronological"
            title="통장내역 순서대로 정렬 (등록 순서)"
          >
            <v-icon size="12">mdi-sort-ascending</v-icon>
            <span>통장순</span>
          </button>
          <label class="quick-filter-item" :class="{ active: filterNeedCheck }">
            <input type="checkbox" v-model="filterNeedCheck" />
            <v-icon size="12" :color="filterNeedCheck ? '#f59e0b' : '#94a3b8'">mdi-alert</v-icon>
            <span>확인필요 ({{ needReviewCount }})</span>
          </label>
          <button 
            v-if="filterGubun !== 'all' || filterNeedCheck || searchScode || searchMemo || searchPrice" 
            class="quick-filter-reset"
            @click="resetFilters"
          >
            <v-icon size="12">mdi-refresh</v-icon>
          </button>
        </div>

        <!-- 액션 버튼 영역 -->
        <div class="sidebar-actions">
          <button class="action-btn add" @click="autoDialogOpen = true">
            <v-icon size="16">mdi-plus</v-icon>
            항목 추가
          </button>
          <!-- 확정하기 버튼 (확정 전에만) -->
          <!-- 확정하기 버튼 (확정 전) -->
          <button 
            v-if="!isConfirmed && inoutList.length > 0"
            class="action-btn confirm" 
            @click="openConfirmDialog"
            :disabled="isFillingRelAcc || !allScodesFilled"
            :title="!allScodesFilled ? `계정이 없는 항목 ${missingScodeCount}건이 있습니다. 먼저 모든 계정을 채워주세요.` : '확정하면 수정/삭제가 불가능합니다'"
          >
            <v-icon size="16">mdi-lock-check</v-icon>
            확정하기
          </button>
          <!-- 확정해제 버튼 (확정 후) -->
          <button 
            v-if="isConfirmed"
            class="action-btn unconfirm" 
            @click="handleUnconfirm"
          >
            <v-icon size="16">mdi-lock-open</v-icon>
            확정해제
          </button>
          <!-- 모두삭제 버튼 (확정 전에만) -->
          <button 
            v-if="!isConfirmed && filteredInoutList.length > 0" 
            class="action-btn delete-all" 
            @click="deleteAllDialogOpen = true"
          >
            <v-icon size="16">mdi-delete-sweep</v-icon>
            모두삭제 ({{ filteredInoutList.length }}건)
          </button>
          <button 
            v-if="canManageLearning"
            class="action-btn learn" 
            @click="openDictionaryManageModal"
          >
            <v-icon size="16">mdi-brain</v-icon>
            AI 학습 관리
          </button>
        </div>
      </aside>
      
      <!-- 우측: 메인 테이블 -->
      <main class="inout-main-content">
        <v-card class="table-card">
          <v-card-text class="table-card-text">
            <!-- 테이블 -->
            <div class="inout-table-wrapper">
              <table class="inout-table" v-if="filteredInoutList.length > 0">
                <thead>
                  <tr>
                    <th class="th-date sortable" @click="toggleSort('hi_inout_date')">
                      날짜
                      <v-icon size="14" class="sort-icon">{{ getSortIcon('hi_inout_date') }}</v-icon>
                    </th>
                    <th class="th-gubun sortable" @click="toggleSort('hi_gubun')">
                      구분
                      <v-icon size="14" class="sort-icon">{{ getSortIcon('hi_gubun') }}</v-icon>
                    </th>
                    <th class="th-scode sortable" @click="toggleSort('hi_scode')">
                      계정코드
                      <v-icon size="14" class="sort-icon">{{ getSortIcon('hi_scode') }}</v-icon>
                    </th>
                    <th class="th-price sortable" @click="toggleSort('hi_price')">
                      금액
                      <v-icon size="14" class="sort-icon">{{ getSortIcon('hi_price') }}</v-icon>
                    </th>
                    <th class="th-memo sortable" @click="toggleSort('hi_memo')">
                      적요
                      <v-icon size="14" class="sort-icon">{{ getSortIcon('hi_memo') }}</v-icon>
                    </th>
                    <th class="th-actions">기능</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="item in filteredInoutList" :key="item.hi_idx">
                  <tr 
                    :class="{ 
                      'row-highlighted': item.hi_idx && highlightedIdxList.includes(item.hi_idx),
                      'row-editing': editingRowIdx === item.hi_idx,
                      'row-confirmed': isConfirmed
                    }"
                  >
                    <td class="td-date">{{ formatDate(item.hi_inout_date) }}</td>
                    <td class="td-gubun">
                      <span 
                        class="gubun-badge" 
                        :class="item.hi_gubun === 'IN' ? 'income' : 'expense'"
                      >
                        {{ item.hi_gubun === 'IN' ? '수입' : '지출' }}
                      </span>
                    </td>
                    <td class="td-scode" @click.stop>
                      <!-- 수정 모드 -->
                      <template v-if="editingRowIdx === item.hi_idx">
                        <v-autocomplete
                          v-model="inlineEditData.hi_scode"
                          :items="item.hi_gubun === 'IN' ? incomeCodeList : expenseCodeList"
                          item-title="fc_sname"
                          item-value="fc_scode"
                          variant="outlined"
                          density="compact"
                          hide-details
                          placeholder="계정 선택"
                          class="inline-edit-select"
                          @keydown.esc="cancelInlineEdit"
                        />
                      </template>
                      <!-- 보기 모드 -->
                      <template v-else>
                        <span v-if="item.hi_scode && item.hi_scode.trim()" class="scode-name">{{ getScodeDisplayName(item.hi_scode, item.hi_gubun) }}</span>
                        <span v-else class="scode-missing">
                          <v-icon size="14" color="#f59e0b">mdi-alert</v-icon>
                          미설정
                        </span>
                      </template>
                    </td>
                    <td class="td-price" :class="[item.hi_gubun === 'IN' ? 'income' : 'expense', { 'missing': !item.hi_price || item.hi_price <= 0 }]" @click.stop>
                      <!-- 수정 모드 -->
                      <template v-if="editingRowIdx === item.hi_idx">
                        <div class="inline-price-input">
                          <input
                            type="text"
                            :value="formatNumber(inlineEditData.hi_price)"
                            @input="handleInlinePriceInput"
                            @keydown="handleInlinePriceKeydown"
                            class="inline-price-field"
                          />
                          <span class="inline-price-unit">원</span>
                        </div>
                      </template>
                      <!-- 보기 모드 -->
                      <template v-else>
                        <template v-if="item.hi_price && item.hi_price > 0">
                          {{ formatNumber(item.hi_price) }}원
                        </template>
                        <span v-else class="price-missing">
                          <v-icon size="14" color="#f59e0b">mdi-alert</v-icon>
                          미설정
                        </span>
                      </template>
                    </td>
                    <td class="td-memo" @click.stop>
                      <!-- 수정 모드 -->
                      <template v-if="editingRowIdx === item.hi_idx">
                        <input
                          type="text"
                          v-model="inlineEditData.hi_memo"
                          @keydown.enter="saveInlineEdit"
                          @keydown.esc="cancelInlineEdit"
                          class="inline-memo-input"
                          placeholder="적요 입력"
                        />
                      </template>
                      <!-- 보기 모드 -->
                      <template v-else>
                        <div class="memo-container">
                          <span v-if="item.hi_memo && item.hi_memo.trim()" class="memo-text">{{ item.hi_memo }}</span>
                          <span v-else class="memo-missing">
                            <v-icon size="14" color="#f59e0b">mdi-alert</v-icon>
                            미설정
                          </span>
                          <!-- 확인필요 이유 표시 -->
                          <span v-if="getReviewReason(item)" class="review-reason-tag" :title="getReviewReason(item) || ''">
                            <v-icon size="12" color="#f59e0b">mdi-alert-circle</v-icon>
                            {{ getReviewReason(item) }}
                          </span>
                        </div>
                      </template>
                    </td>
                    <td class="td-actions" @click.stop>
                      <div class="action-buttons">
                        <!-- 수정 모드: 저장/취소 버튼 -->
                        <template v-if="editingRowIdx === item.hi_idx">
                          <button 
                            class="action-btn save" 
                            @click="saveInlineEdit"
                            :disabled="isInlineSaving"
                            title="저장 (Enter)"
                          >
                            <v-icon size="16">mdi-content-save</v-icon>
                          </button>
                          <button 
                            class="action-btn cancel" 
                            @click="cancelInlineEdit"
                            title="취소 (Esc)"
                          >
                            <v-icon size="16">mdi-close</v-icon>
                          </button>
                        </template>
                        <!-- 일반 모드: 수정/삭제 버튼 -->
                        <template v-else>
                          <template v-if="!isConfirmed">
                            <button 
                              class="action-btn edit" 
                              @click="startInlineEdit(item, $event)" 
                              title="수정"
                            >
                              <v-icon size="16">mdi-pencil</v-icon>
                            </button>
                            <button 
                              class="action-btn delete" 
                              @click="confirmDelete(item)" 
                              title="삭제"
                            >
                              <v-icon size="16">mdi-delete</v-icon>
                            </button>
                          </template>
                          <v-icon v-else size="16" color="#9ca3af" title="확정된 데이터는 수정할 수 없습니다">mdi-lock</v-icon>
                        </template>
                      </div>
                    </td>
                  </tr>
                  <!-- 수정 모드: 학습 정보 확장 행 -->
                  <tr 
                    v-if="editingRowIdx === item.hi_idx" 
                    class="learning-info-row"
                  >
                    <td colspan="6">
                      <div class="learning-info-panel">
                        <div class="learning-header">
                          <v-icon size="16" color="#6366f1">mdi-brain</v-icon>
                          <span>AI 학습 관리</span>
                        </div>
                        
                        <!-- 직접 키워드 추가 영역 (계정이 선택된 경우에만 표시) -->
                        <div v-if="inlineEditData.hi_scode" class="keyword-add-section">
                          <div class="keyword-add-form">
                            <input 
                              type="text"
                              v-model="newKeywordInput"
                              @keydown.enter="addCustomKeyword"
                              placeholder="새 키워드 입력..."
                              class="keyword-input"
                            />
                            <button 
                              class="keyword-add-btn"
                              @click="addCustomKeyword"
                              :disabled="!newKeywordInput.trim() || isAddingKeyword"
                            >
                              <v-progress-circular 
                                v-if="isAddingKeyword" 
                                indeterminate 
                                size="14" 
                                width="2" 
                              />
                              <v-icon v-else size="16">mdi-plus</v-icon>
                              추가
                            </button>
                          </div>
                          <div class="keyword-add-hint">
                            <v-icon size="12" color="#94a3b8">mdi-information-outline</v-icon>
                            <span>키워드를 입력하면 <strong>{{ getScodeDisplayName(inlineEditData.hi_scode, inlineEditData.hi_gubun) }}</strong> 계정에 학습됩니다</span>
                          </div>
                        </div>
                        
                        <!-- 미학습 키워드 학습 영역 (계정이 선택된 경우에만 표시) -->
                        <div v-if="inlineEditData.hi_scode && unlearnedKeywords.length > 0" class="unlearned-keywords-section">
                          <div class="unlearned-header">
                            <v-icon size="14" color="#f59e0b">mdi-lightbulb-on</v-icon>
                            <span>적요에서 추출된 키워드</span>
                            <button 
                              v-if="unlearnedKeywords.length > 1"
                              class="learn-all-btn"
                              @click="learnAllUnlearnedKeywords"
                              :disabled="learningKeyword !== null"
                            >
                              <v-icon size="14">mdi-check-all</v-icon>
                              모두 학습
                            </button>
                          </div>
                          <div class="unlearned-keywords-list">
                            <button 
                              v-for="kw in unlearnedKeywords" 
                              :key="kw"
                              class="unlearned-keyword-chip"
                              :class="{ 'learning': learningKeyword === kw }"
                              @click="learnKeywordToCurrentAccount(kw)"
                              :disabled="learningKeyword !== null"
                              :title="`'${kw}' 키워드를 현재 계정에 학습`"
                            >
                              <v-progress-circular 
                                v-if="learningKeyword === kw" 
                                indeterminate 
                                size="12" 
                                width="2" 
                                color="white" 
                              />
                              <v-icon v-else size="14">mdi-plus</v-icon>
                              <span>{{ kw }}</span>
                            </button>
                          </div>
                        </div>
                        
                        <!-- 로딩 -->
                        <div v-if="isLoadingLearningInfo" class="learning-loading">
                          <v-progress-circular indeterminate size="18" width="2" color="primary" />
                          <span>학습 정보 조회 중...</span>
                        </div>
                        
                        <!-- 학습된 정보 목록 -->
                        <template v-else>
                          <div v-if="inlineLearningInfo.length > 0" class="learning-items">
                            <div class="learning-items-header">
                              <v-icon size="14" color="#10b981">mdi-check-circle</v-icon>
                              <span>학습된 키워드 ({{ inlineLearningInfo.length }}개)</span>
                            </div>
                            <div 
                              v-for="lItem in inlineLearningInfo" 
                              :key="lItem.idx"
                              class="learning-item"
                              :class="{ 
                                'global': lItem.scope === '전역',
                                'current-scode': lItem.scode === inlineEditData.hi_scode,
                                'editing': editingKeywordIdx === lItem.idx
                              }"
                            >
                              <!-- 수정 모드 -->
                              <template v-if="editingKeywordIdx === lItem.idx">
                                <div class="learning-item-edit">
                                  <input 
                                    type="text"
                                    v-model="editingKeywordValue"
                                    @keydown.enter="saveKeywordEdit(lItem)"
                                    @keydown.esc="cancelKeywordEdit"
                                    class="keyword-edit-input"
                                    ref="keywordEditInput"
                                  />
                                  <v-icon size="12" color="#94a3b8">mdi-arrow-right</v-icon>
                                  <v-autocomplete
                                    v-model="editingKeywordScode"
                                    :items="inlineEditData.hi_gubun === 'IN' ? incomeCodeList : expenseCodeList"
                                    item-title="fc_sname"
                                    item-value="fc_scode"
                                    variant="plain"
                                    density="compact"
                                    hide-details
                                    placeholder="계정 선택"
                                    class="keyword-edit-select"
                                  />
                                </div>
                                <div class="learning-item-edit-actions">
                                  <button 
                                    class="edit-action-btn save" 
                                    @click="saveKeywordEdit(lItem)"
                                    :disabled="isSavingKeyword"
                                    title="저장"
                                  >
                                    <v-progress-circular 
                                      v-if="isSavingKeyword" 
                                      indeterminate 
                                      size="12" 
                                      width="2" 
                                    />
                                    <v-icon v-else size="14">mdi-check</v-icon>
                                  </button>
                                  <button 
                                    class="edit-action-btn cancel" 
                                    @click="cancelKeywordEdit"
                                    title="취소"
                                  >
                                    <v-icon size="14">mdi-close</v-icon>
                                  </button>
                                </div>
                              </template>
                              
                              <!-- 보기 모드 -->
                              <template v-else>
                                <div class="learning-item-main">
                                  <span class="learning-keyword">{{ lItem.keyword }}</span>
                                  <v-icon size="12" color="#94a3b8">mdi-arrow-right</v-icon>
                                  <span class="learning-scode">{{ lItem.scodeName }}</span>
                                  <span class="learning-scope" :class="lItem.scope === '전역' ? 'global' : 'local'">
                                    {{ lItem.scope }}
                                  </span>
                                </div>
                                <div class="learning-item-meta">
                                  <span class="learning-count">{{ lItem.count }}회</span>
                                  <span v-if="lItem.relAccName" class="learning-relacc">
                                    상대: {{ lItem.relAccName }}
                                  </span>
                                </div>
                                <div class="learning-item-actions">
                                  <button 
                                    class="learning-edit-btn" 
                                    @click="startKeywordEdit(lItem)"
                                    title="수정"
                                  >
                                    <v-icon size="14">mdi-pencil</v-icon>
                                  </button>
                                  <button 
                                    class="learning-delete-btn" 
                                    @click="deleteInlineLearningItem(lItem)"
                                    title="삭제"
                                  >
                                    <v-icon size="14">mdi-delete</v-icon>
                                  </button>
                                </div>
                              </template>
                            </div>
                          </div>
                          
                          <!-- 학습 정보 없음 -->
                          <div v-else-if="!inlineEditData.hi_scode" class="learning-empty">
                            <v-icon size="18" color="#94a3b8">mdi-information-outline</v-icon>
                            <span>계정을 선택하면 키워드를 학습시킬 수 있습니다.</span>
                          </div>
                          <div v-else class="learning-empty">
                            <v-icon size="18" color="#94a3b8">mdi-information-outline</v-icon>
                            <span>학습된 키워드가 없습니다. 위에서 키워드를 추가하세요.</span>
                          </div>
                        </template>
                      </div>
                    </td>
                  </tr>
                  </template>
                </tbody>
              </table>
              
              <!-- 빈 데이터 (필터로 인해 비어있는 경우) -->
              <div v-else-if="inoutList.length > 0 && filteredInoutList.length === 0" class="no-data-container filtered">
                <v-icon size="64" color="#f59e0b">mdi-filter-off</v-icon>
                <p>{{ getFilteredEmptyMessage }}</p>
                <button class="filter-reset-btn" @click="resetFilters">
                  <v-icon size="18">mdi-filter-remove</v-icon>
                  필터 해제하고 전체 보기 ({{ inoutList.length }}건)
                </button>
              </div>
              
              <!-- 빈 데이터 (실제로 데이터가 없는 경우) -->
              <div v-else class="no-data-container">
                <v-icon size="64" color="grey-lighten-1">mdi-file-document-outline</v-icon>
                <p>등록된 현금출납 내역이 없습니다.</p>
                <v-btn color="primary" variant="tonal" @click="autoDialogOpen = true">
                  첫 항목 추가하기
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </main>
    </div>
    
    <!-- 항목 추가 다이얼로그 -->
    <PgmInoutAuto 
      v-model="autoDialogOpen"
      :current-year="currentYear"
      :current-month="currentMonth"
      @saved="handleAutoUploadSaved"
    />
    
    <!-- 삭제 확인 다이얼로그 -->
    <v-dialog v-model="deleteDialogOpen" max-width="400">
      <div class="delete-modal">
        <div class="delete-modal-header">
          <div class="delete-icon">
            <v-icon size="28" color="white">mdi-alert</v-icon>
          </div>
          <h3>삭제 확인</h3>
          <p>이 작업은 되돌릴 수 없습니다</p>
        </div>
        <div class="delete-modal-body">
          <p class="delete-message">선택한 현금출납 항목을 삭제하시겠습니까?</p>
          <div class="delete-info" v-if="selectedItem">
            <div class="info-row">
              <span class="info-label">날짜</span>
              <span class="info-value">{{ formatDate(selectedItem.hi_inout_date) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">구분</span>
              <span class="info-value" :class="selectedItem.hi_gubun === 'IN' ? 'income' : 'expense'">
                {{ selectedItem.hi_gubun === 'IN' ? '수입' : '지출' }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">금액</span>
              <span class="info-value">{{ formatNumber(selectedItem.hi_price) }}원</span>
            </div>
          </div>
        </div>
        <div class="delete-modal-footer">
          <button type="button" class="btn-cancel" @click="deleteDialogOpen = false">취소</button>
          <button 
            type="button" 
            class="btn-delete" 
            @click="deleteInout"
            :disabled="isDeleting"
          >
            <v-progress-circular v-if="isDeleting" indeterminate size="18" width="2" color="white" />
            <span v-else>삭제하기</span>
          </button>
        </div>
      </div>
    </v-dialog>
    
    <!-- 모두삭제 확인 다이얼로그 -->
    <v-dialog v-model="deleteAllDialogOpen" max-width="450" persistent>
      <div class="delete-modal delete-all-modal">
        <div class="delete-modal-header">
          <div class="delete-icon danger">
            <v-icon size="28" color="white">mdi-delete-sweep</v-icon>
          </div>
          <h3>일괄 삭제 확인</h3>
          <p>이 작업은 되돌릴 수 없습니다</p>
        </div>
        <div class="delete-modal-body">
          <p class="delete-message">
            현재 필터링된 <strong class="count">{{ filteredInoutList.length }}건</strong>의 항목을 모두 삭제하시겠습니까?
          </p>
          <div class="delete-summary">
            <div class="summary-row">
              <span class="label">수입 항목</span>
              <span class="value income">{{ filteredInoutList.filter(i => i.hi_gubun === 'IN').length }}건</span>
            </div>
            <div class="summary-row">
              <span class="label">지출 항목</span>
              <span class="value expense">{{ filteredInoutList.filter(i => i.hi_gubun === 'OUT').length }}건</span>
            </div>
            <div class="summary-row total">
              <span class="label">총 삭제 금액</span>
              <span class="value">{{ formatNumber(filteredInoutList.reduce((sum, i) => sum + (i.hi_price || 0), 0)) }}원</span>
            </div>
          </div>
          <div class="warning-box">
            <v-icon size="16" color="#f59e0b">mdi-alert-circle</v-icon>
            <span>삭제 후 복구가 불가능합니다. 신중하게 결정해주세요.</span>
          </div>
        </div>
        <div class="delete-modal-footer">
          <button type="button" class="btn-cancel" @click="deleteAllDialogOpen = false">취소</button>
          <button 
            type="button" 
            class="btn-delete danger" 
            @click="deleteAllItems"
            :disabled="isDeleting"
          >
            <v-progress-circular v-if="isDeleting" indeterminate size="18" width="2" color="white" />
            <span v-else>{{ filteredInoutList.length }}건 모두 삭제</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- AI 추론 정보 모달 (회계 실무자 관점) -->
    <v-dialog v-model="learningModalOpen" max-width="520" persistent>
      <div class="learning-modal" v-if="learningItem">
        <div class="modal-header learning">
          <div class="header-icon">
            <v-icon size="24" color="white">mdi-brain</v-icon>
          </div>
          <div class="header-text">
            <h3>AI 추론 결과</h3>
            <p>{{ learningItem.hi_memo }} · {{ formatNumber(learningItem.hi_price) }}원</p>
          </div>
          <button class="close-btn" @click="learningModalOpen = false">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
        
        <div class="modal-body">
          <!-- 추론 상태 표시 (reviewReason 기준) -->
          <div class="inference-status" :class="parsedNeedCheck?.reviewReason ? 'need-check' : 'ok'" v-if="parsedNeedCheck">
            <v-icon size="18">{{ parsedNeedCheck.reviewReason ? 'mdi-alert-circle' : 'mdi-check-circle' }}</v-icon>
            <span v-if="!parsedNeedCheck.reviewReason">추론 완료</span>
            <span v-else>확인 필요 - 아래 내용을 검토해주세요</span>
          </div>

          <!-- 추론 결과 카드 (인라인 수정 가능) -->
          <div class="inference-result-card editable">
            <div class="result-item">
              <div class="result-header">
                <span class="result-type" :class="learningItem.hi_gubun === 'IN' ? 'income' : 'expense'">
                  {{ learningItem.hi_gubun === 'IN' ? '수입' : '지출' }}
                </span>
                <span class="result-label">계정</span>
                <span class="confidence-badge" v-if="parsedNeedCheck?.prediction?.probability" :class="getProbabilityClass(parsedNeedCheck.prediction.probability)">
                  {{ parsedNeedCheck.prediction.probability }}%
                </span>
              </div>
              <div class="result-value-editable">
                <v-autocomplete
                  v-model="learningItem.hi_scode"
                  :items="learningItem.hi_gubun === 'IN' ? incomeCodeList : expenseCodeList"
                  item-title="fc_sname"
                  item-value="fc_scode"
                  density="compact"
                  variant="outlined"
                  hide-details
                  placeholder="계정 선택"
                  class="inline-autocomplete"
                  @update:model-value="onLearningScodeChange"
                />
              </div>
            </div>

            <div class="result-item" v-if="learningItem.hi_gubun === 'OUT'">
              <div class="result-header">
                <span class="result-type income">수입</span>
                <span class="result-label">상대계정 (자금 출처)</span>
                <span 
                  v-if="learningItem.hi_rel_acc && getRelAccPercentage(learningItem.hi_rel_acc) > 0" 
                  class="probability-badge"
                >
                  {{ getRelAccPercentage(learningItem.hi_rel_acc) }}%
                </span>
              </div>
              <div class="result-value-editable">
                <v-autocomplete
                  v-model="learningItem.hi_rel_acc"
                  :items="relAccOptionsWithBalance"
                  item-title="displayName"
                  item-value="scode"
                  density="compact"
                  variant="outlined"
                  hide-details
                  placeholder="상대계정 선택"
                  class="inline-autocomplete"
                  clearable
                >
                  <template #item="{ props, item }">
                    <v-list-item v-bind="props" :title="undefined">
                      <div class="rel-acc-option">
                        <span class="rel-acc-name">{{ item.raw.sname }}</span>
                        <span class="rel-acc-balance" :class="{ 'no-balance': item.raw.availableBalance <= 0 }">
                          {{ formatNumber(item.raw.availableBalance) }}원
                        </span>
                      </div>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </div>
            </div>
          </div>

          <!-- 상대계정 분포 (해당 계정일 때 상대계정 사용 빈도) - 실시간 조회 -->
          <div class="rel-acc-distribution" v-if="(realTimeRelAccDistribution.length > 0 || (parsedNeedCheck?.prediction?.relAccDistribution?.length ?? 0) > 0) && learningItem?.hi_gubun === 'OUT'">
            <h4>
              <v-icon size="16">mdi-chart-pie</v-icon>
              이 계정의 상대계정 분포
            </h4>
            <div class="distribution-list">
              <div 
                v-for="(dist, idx) in (realTimeRelAccDistribution.length > 0 ? realTimeRelAccDistribution : parsedNeedCheck?.prediction?.relAccDistribution || [])" 
                :key="idx"
                class="distribution-item"
                :class="{ 'current': learningItem?.hi_rel_acc === dist.rel_acc }"
              >
                <span class="dist-name">{{ dist.sname }}</span>
                <div class="dist-bar-container">
                  <div class="dist-bar" :style="{ width: dist.percentage + '%' }"></div>
                </div>
                <span class="dist-percent">{{ dist.percentage }}%</span>
                <span class="dist-count">({{ dist.count }}건)</span>
              </div>
            </div>
          </div>

          <!-- ★ 확인필요 이유 (reviewReason이 있는 경우) -->
          <div class="inference-reason review-needed" v-if="parsedNeedCheck?.reviewReason">
            <h4>
              <v-icon size="16" color="warning">mdi-alert-circle-outline</v-icon>
              확인 필요 이유
            </h4>
            <div class="review-reason-box">
              {{ parsedNeedCheck.reviewReason }}
            </div>
            <p class="reason-notice">
              <v-icon size="14" color="warning">mdi-information</v-icon>
              계정을 확인/수정 후 "저장 및 학습"을 눌러 학습시켜 주세요.
            </p>
          </div>
          
          <!-- 추론 근거 (reviewReason이 없는 경우에만 표시) -->
          <div class="inference-reason" v-else-if="parsedNeedCheck">
            <h4>
              <v-icon size="16">mdi-lightbulb-outline</v-icon>
              추론 근거
            </h4>
            <ul class="reason-list">
              <li v-if="parsedNeedCheck.prediction?.totalCount && parsedNeedCheck.prediction.totalCount > 0">
                과거 유사 거래 {{ parsedNeedCheck.prediction?.totalCount }}건 분석
              </li>
              <li v-if="parsedNeedCheck.prediction?.relAccReason">
                {{ getSimplifiedRelAccReason(parsedNeedCheck.prediction.relAccReason) }}
              </li>
              <li v-if="parsedNeedCheck.reason && !parsedNeedCheck.reason.includes('키워드')">
                {{ getSimplifiedReason(parsedNeedCheck.reason) }}
              </li>
              <li v-if="!parsedNeedCheck.prediction?.totalCount && !parsedNeedCheck.prediction?.relAccReason">
                학습된 데이터가 부족하여 정확도가 낮을 수 있습니다
              </li>
            </ul>
          </div>

          <!-- 수정 영역 -->
          
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-cancel" @click="learningModalOpen = false">취소</button>
          <button 
            type="button" 
            class="btn-save" 
            @click="checkSimilarAndSave"
            :disabled="isSavingLearning"
          >
            <v-progress-circular v-if="isSavingLearning" indeterminate size="18" width="2" color="white" />
            <span v-else>저장 및 학습</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- 유사 항목 적용 확인 모달 -->
    <v-dialog v-model="applySimilarConfirmOpen" max-width="480" persistent>
      <div class="apply-similar-modal">
        <div class="modal-header">
          <h3>
            <v-icon size="small">mdi-content-copy</v-icon>
            유사 항목 발견
          </h3>
          <button class="close-btn" @click="applySimilarConfirmOpen = false">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="similar-info">
            <v-icon size="48" color="primary" class="info-icon">mdi-file-document-multiple-outline</v-icon>
            <p class="similar-message">
              적요와 금액이 비슷한 <strong>{{ similarItemsCount }}개</strong>의 
              <br>미분류 항목이 발견되었습니다.
            </p>
            <p class="similar-hint">
              같은 계정을 모든 유사 항목에 적용하시겠습니까?
            </p>
          </div>
        </div>
        
        <div class="modal-footer apply-similar-footer">
          <button 
            type="button" 
            class="btn-secondary"
            @click="saveLearningData(false)"
          >
            해당 항목만 저장
          </button>
          <button 
            type="button" 
            class="btn-primary"
            @click="saveLearningData(true)"
          >
            <v-icon size="18">mdi-check-all</v-icon>
            모든 유사 항목에 적용 ({{ similarItemsCount }}건)
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- 금액 범위 조정 모달 -->
    <v-dialog v-model="rangeAdjustModalOpen" max-width="450" persistent>
      <div class="range-adjust-modal">
        <div class="modal-header">
          <h3>
            <v-icon size="small">mdi-tune</v-icon>
            금액 범위 조정
          </h3>
          <button class="close-btn" @click="rangeAdjustModalOpen = false">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="info-section">
            <div class="info-row">
              <span class="info-label">계정코드:</span>
              <span class="info-value">{{ rangeAdjustData.scodeName }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">관련 키워드:</span>
              <span class="info-value">{{ rangeAdjustData.keyword }}</span>
            </div>
          </div>
          
          <div class="range-section">
            <div class="current-range">
              <span class="section-title">현재 학습된 범위</span>
              <div class="range-display">
                <span>{{ formatNumber(rangeAdjustData.currentMin) }}원</span>
                <span class="range-separator">~</span>
                <span>{{ formatNumber(rangeAdjustData.currentMax) }}원</span>
              </div>
            </div>
            
            <div class="new-range">
              <span class="section-title">새로운 범위 설정</span>
              <div class="range-inputs">
                <div class="input-group">
                  <label>최소 금액</label>
                  <input 
                    type="number" 
                    v-model.number="rangeAdjustData.newMin"
                    :max="rangeAdjustData.newMax"
                  />
                </div>
                <span class="range-separator">~</span>
                <div class="input-group">
                  <label>최대 금액</label>
                  <input 
                    type="number" 
                    v-model.number="rangeAdjustData.newMax"
                    :min="rangeAdjustData.newMin"
                  />
                </div>
              </div>
              <div class="quick-actions">
                <button 
                  class="quick-btn" 
                  @click="rangeAdjustData.newMin = Math.min(rangeAdjustData.currentMin, learningItem?.hi_price || 0)"
                  title="현재 입력값을 최소값에 포함"
                >
                  <v-icon size="x-small">mdi-arrow-down</v-icon>
                  입력값 포함 (최소)
                </button>
                <button 
                  class="quick-btn" 
                  @click="rangeAdjustData.newMax = Math.max(rangeAdjustData.currentMax, learningItem?.hi_price || 0)"
                  title="현재 입력값을 최대값에 포함"
                >
                  <v-icon size="x-small">mdi-arrow-up</v-icon>
                  입력값 포함 (최대)
                </button>
              </div>
            </div>
          </div>
          
          <div class="notice-box">
            <v-icon size="small" color="info">mdi-information</v-icon>
            <span>범위를 조정하면 해당 계정코드의 모든 관련 키워드에 적용됩니다.</span>
          </div>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn-cancel" @click="rangeAdjustModalOpen = false">취소</button>
          <button type="button" class="btn-save" @click="saveRangeAdjust">범위 저장</button>
        </div>
      </div>
    </v-dialog>

    <!-- AI 학습 관리 모달 -->
    <v-dialog v-model="dictionaryManageModalOpen" max-width="1020" persistent>
      <div class="dictionary-manage-modal">
        <div class="modal-header learning">
          <div class="header-icon">
            <v-icon size="24" color="white">mdi-brain</v-icon>
          </div>
          <div class="header-text">
            <h3>AI 학습 관리</h3>
            <p>학습 데이터 현황 조회</p>
          </div>
          <button class="close-btn" @click="dictionaryManageModalOpen = false">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>

        <div class="modal-body">
          <!-- 현재 학습 통계 (클릭 가능) -->
          <div class="dictionary-stats" v-if="dictionaryStats">
            <h4>
              <v-icon size="16">mdi-chart-bar</v-icon>
              학습 데이터 현황
              <span class="hint">(카드를 클릭하면 상세 내역을 볼 수 있습니다)</span>
            </h4>
            <div class="stats-grid">
              <div 
                class="stat-item clickable" 
                :class="{ active: selectedDictTable === key }"
                v-for="(stat, key) in dictionaryStats" 
                :key="key"
                @click="selectDictTable(key as string)"
              >
                <span class="stat-label">{{ stat.label }}</span>
                <span class="stat-value">{{ formatNumber(stat.count) }}건</span>
                <v-icon size="12" class="expand-icon">mdi-chevron-right</v-icon>
              </div>
            </div>
          </div>

          <!-- 선택된 테이블의 상세 목록 -->
          <div class="dict-detail-section" v-if="selectedDictTable">
            <div class="detail-header">
              <h4>
                <v-icon size="16">mdi-format-list-bulleted</v-icon>
                {{ getDictTableLabel(selectedDictTable) }} 상세
              </h4>
              <button class="close-detail-btn" @click="selectedDictTable = null">
                <v-icon size="14">mdi-close</v-icon>
              </button>
            </div>
            
            <!-- 로딩 -->
            <div v-if="isDictDetailLoading" class="detail-loading">
              <v-progress-circular indeterminate size="24" width="2" color="primary" />
              <span>데이터 로딩 중...</span>
            </div>
            
            <!-- 테이블 헤더: 수입/지출은 검색창, 나머지는 설명 -->
            <div v-if="selectedDictTable && !isDictDetailLoading" class="detail-header-desc">
              <template v-if="selectedDictTable === 'income' || selectedDictTable === 'expense'">
                <div class="dict-search-wrap">
                  <v-icon size="18" class="search-icon">mdi-magnify</v-icon>
                  <input 
                    type="text" 
                    v-model="dictSearchQuery"
                    :placeholder="selectedDictTable === 'income' ? '수입 계정명 또는 키워드 검색...' : '지출 계정명 또는 키워드 검색...'"
                    class="dict-search-input"
                  />
                  <button v-if="dictSearchQuery" class="search-clear-btn" @click="dictSearchQuery = ''">
                    <v-icon size="16">mdi-close</v-icon>
                  </button>
                </div>
              </template>
              <template v-else-if="selectedDictTable === 'scodeRelmap'">
                <p class="desc-text"><strong>지출 계정</strong>을 사용할 때 주로 <strong>어떤 수입 계정</strong>에서 금액을 차감했는지 기록입니다</p>
              </template>
              <template v-else-if="selectedDictTable === 'scodeStats'">
                <p class="desc-text">각 <strong>계정별</strong>로 사용된 평균 금액과 횟수 통계입니다</p>
              </template>
            </div>
            
            <!-- 수입/지출 딕셔너리: 계정별 그룹화된 뷰 -->
            <div v-if="!isDictDetailLoading && (selectedDictTable === 'income' || selectedDictTable === 'expense')" class="grouped-dict-container">
              <div class="dict-action-header">
                <button class="btn-add-item" @click="openNewAccountDialog">
                  <v-icon size="16">mdi-plus</v-icon>
                  계정 추가
                </button>
              </div>
              <div v-if="filteredDictGroupedList.length > 0" class="grouped-dict-view">
                <div 
                  v-for="(group, gIdx) in filteredDictGroupedList" 
                  :key="`group-${group.scode}-${gIdx}`"
                  class="account-group"
                >
                  <div class="group-header">
                    <div class="group-title">
                      <span class="account-name">{{ group.scodeName || group.scode || '(계정명 없음)' }}</span>
                      <span class="account-code" v-if="group.scodeName && group.scode">({{ group.scode }})</span>
                      <span class="total-count">총 {{ group.totalCount || 0 }}회 학습</span>
                    </div>
                    <button class="btn-add-keyword" @click="openAddKeywordDialog(group)" title="키워드 추가">
                      <v-icon size="16">mdi-plus</v-icon>
                    </button>
                  </div>
                  <div class="keywords-wrap">
                    <div 
                      v-for="(kw, kIdx) in group.keywords" 
                      :key="`kw-${kw.idx || kIdx}`"
                      class="keyword-item"
                      @click="openEditKeywordDialog(kw, group)"
                    >
                      <span class="kw-text">{{ kw.keyword || '(키워드 없음)' }}</span>
                      <span class="kw-count">{{ kw.count || 0 }}</span>
                      <button class="kw-delete" @click.stop="deleteKeyword(kw)" title="삭제">
                        <v-icon size="12">mdi-close</v-icon>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <!-- 검색 결과 없음 -->
              <div v-else-if="dictGroupedList.length > 0 && filteredDictGroupedList.length === 0" class="detail-empty">
                <v-icon size="32" color="grey-lighten-1">mdi-magnify</v-icon>
                <p>"{{ dictSearchQuery }}" 검색 결과가 없습니다.</p>
                <p class="empty-hint">다른 검색어를 입력해보세요.</p>
              </div>
              <!-- 데이터 없음 -->
              <div v-else class="detail-empty">
                <v-icon size="32" color="grey-lighten-1">mdi-database-off</v-icon>
                <p>학습된 데이터가 없습니다.</p>
                <p class="empty-hint">위의 "계정 추가" 버튼으로 새로운 계정과 키워드를 등록하세요.</p>
              </div>
            </div>
            
            <!-- 계정관계맵: 테이블 형식 목록 -->
            <div v-if="!isDictDetailLoading && selectedDictTable === 'scodeRelmap'" class="detail-table-wrap">
              <div class="detail-table-header">
                <button class="btn-add-item" @click="openRelmapDialog('add')">
                  <v-icon size="16">mdi-plus</v-icon>
                  매핑 추가
                </button>
              </div>
              <table class="detail-table" v-if="dictDetailList.length > 0">
                <thead>
                  <tr>
                    <th class="col-account">지출 계정</th>
                    <th class="col-relacc">주로 사용하는 수입 계정</th>
                    <th class="col-count">사용 횟수</th>
                    <th class="col-action"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="(item, idx) in dictDetailList" 
                    :key="`rel-${item.idx || idx}`"
                    class="clickable-row"
                    @click="openRelmapDialog('edit', item)"
                  >
                    <td class="col-account">{{ item.scodeName || item.scode }}</td>
                    <td class="col-relacc">{{ item.relAccName || item.relAcc }}</td>
                    <td class="col-count"><span class="count-badge">{{ item.count }}회</span></td>
                    <td class="col-action">
                      <button class="btn-action btn-edit" @click.stop="openRelmapDialog('edit', item)" title="수정">
                        <v-icon size="16">mdi-pencil</v-icon>
                      </button>
                      <button class="btn-action btn-delete" @click.stop="deleteDictItem(item)" title="삭제">
                        <v-icon size="16">mdi-delete</v-icon>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="detail-empty">
                <v-icon size="32" color="grey-lighten-1">mdi-database-off</v-icon>
                <p>학습된 데이터가 없습니다.</p>
              </div>
            </div>
            
            <!-- 계정별 통계: 테이블 형식 목록 -->
            <div v-if="!isDictDetailLoading && selectedDictTable === 'scodeStats'" class="detail-table-wrap">
              <div class="detail-table-header">
                <button class="btn-add-item" @click="openStatsDialog('add')">
                  <v-icon size="16">mdi-plus</v-icon>
                  통계 추가
                </button>
              </div>
              <table class="detail-table" v-if="dictDetailList.length > 0">
                <thead>
                  <tr>
                    <th class="col-account">계정명</th>
                    <th class="col-gubun">구분</th>
                    <th class="col-avg">평균 금액</th>
                    <th class="col-range">금액 범위</th>
                    <th class="col-count">사용 횟수</th>
                    <th class="col-action"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="(item, idx) in dictDetailList" 
                    :key="`stat-${item.idx || idx}`"
                    class="clickable-row"
                    @click="openStatsDialog('edit', item)"
                  >
                    <td class="col-account">{{ item.scodeName || item.scode }}</td>
                    <td class="col-gubun">
                      <span :class="['gubun-badge', item.gubun === 'IN' ? 'income' : 'expense']">
                        {{ item.gubun === 'IN' ? '수입' : '지출' }}
                      </span>
                    </td>
                    <td class="col-avg">{{ formatNumber(item.avgAmount || 0) }}원</td>
                    <td class="col-range">{{ formatNumber(item.minMoney || 0) }} ~ {{ formatNumber(item.maxMoney || 0) }}원</td>
                    <td class="col-count"><span class="count-badge">{{ item.count }}회</span></td>
                    <td class="col-action">
                      <button class="btn-action btn-edit" @click.stop="openStatsDialog('edit', item)" title="수정">
                        <v-icon size="16">mdi-pencil</v-icon>
                      </button>
                      <button class="btn-action btn-delete" @click.stop="deleteDictItem(item)" title="삭제">
                        <v-icon size="16">mdi-delete</v-icon>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="detail-empty">
                <v-icon size="32" color="grey-lighten-1">mdi-database-off</v-icon>
                <p>학습된 데이터가 없습니다.</p>
              </div>
            </div>
            
          </div>

        </div>

        <div class="modal-footer">
          <button type="button" class="btn-cancel" @click="dictionaryManageModalOpen = false">
            닫기
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- 키워드 추가/수정 다이얼로그 -->
    <v-dialog v-model="keywordDialogOpen" max-width="450" persistent>
      <div class="keyword-dialog">
        <div class="dialog-header">
          <h4>{{ keywordDialogMode === 'add' ? (keywordDialogData.isNewAccount ? '계정 및 키워드 추가' : '키워드 추가') : '키워드 수정' }}</h4>
          <button class="close-btn" @click="keywordDialogOpen = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="dialog-body">
          <div class="form-group">
            <label>계정</label>
            <!-- 새 계정 추가 시 계정 선택 -->
            <template v-if="keywordDialogData.isNewAccount">
              <v-autocomplete
                v-model="keywordDialogData.scode"
                :items="selectedDictTable === 'income' ? incomeCodeList : expenseCodeList"
                item-title="fc_sname"
                item-value="fc_scode"
                density="compact"
                variant="outlined"
                hide-details
                placeholder="계정 선택"
                @update:model-value="onAccountSelect"
              />
            </template>
            <!-- 기존 계정에 키워드 추가/수정 시 계정 표시만 -->
            <template v-else>
              <p class="account-display">{{ keywordDialogData.scodeName }} ({{ keywordDialogData.scode }})</p>
            </template>
          </div>
          <div class="form-group">
            <label>키워드</label>
            <input 
              type="text" 
              v-model="keywordDialogData.keyword"
              placeholder="키워드를 입력하세요 (적요에 포함될 텍스트)"
              @keyup.enter="saveKeyword"
            />
            <span class="form-hint">입력한 키워드가 적요에 포함되면 해당 계정으로 분류됩니다.</span>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="btn-cancel" @click="keywordDialogOpen = false">취소</button>
          <button 
            class="btn-save" 
            @click="saveKeyword" 
            :disabled="!keywordDialogData.keyword?.trim() || (keywordDialogData.isNewAccount && !keywordDialogData.scode)"
          >
            {{ keywordDialogMode === 'add' ? '추가' : '수정' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- 상대계정 매핑 추가/수정 다이얼로그 -->
    <v-dialog v-model="relmapDialogOpen" max-width="450" persistent>
      <div class="keyword-dialog">
        <div class="dialog-header">
          <h4>{{ relmapDialogMode === 'add' ? '상대계정 매핑 추가' : '상대계정 매핑 수정' }}</h4>
          <button class="close-btn" @click="relmapDialogOpen = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="dialog-body">
          <div class="form-group">
            <label>지출 계정</label>
            <v-autocomplete
              v-model="relmapDialogData.scode"
              :items="expenseCodeList"
              item-title="fc_sname"
              item-value="fc_scode"
              density="compact"
              variant="outlined"
              hide-details
              placeholder="지출 계정 선택"
              :disabled="relmapDialogMode === 'edit'"
            />
          </div>
          <div class="form-group">
            <label>상대계정 (수입 계정)</label>
            <v-autocomplete
              v-model="relmapDialogData.relAcc"
              :items="incomeCodeList"
              item-title="fc_sname"
              item-value="fc_scode"
              density="compact"
              variant="outlined"
              hide-details
              placeholder="수입 계정 선택"
            />
          </div>
          <div class="form-group">
            <label>사용 횟수</label>
            <input 
              type="number" 
              v-model.number="relmapDialogData.count"
              min="1"
              placeholder="1"
            />
          </div>
        </div>
        <div class="dialog-footer">
          <button class="btn-cancel" @click="relmapDialogOpen = false">취소</button>
          <button 
            class="btn-save" 
            @click="saveRelmap" 
            :disabled="!relmapDialogData.scode || !relmapDialogData.relAcc"
          >
            {{ relmapDialogMode === 'add' ? '추가' : '수정' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- 계정별 통계 추가/수정 다이얼로그 -->
    <v-dialog v-model="statsDialogOpen" max-width="450" persistent>
      <div class="keyword-dialog">
        <div class="dialog-header">
          <h4>{{ statsDialogMode === 'add' ? '계정 통계 추가' : '계정 통계 수정' }}</h4>
          <button class="close-btn" @click="statsDialogOpen = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="dialog-body">
          <div class="form-group">
            <label>구분</label>
            <div class="gubun-radio-group">
              <label class="gubun-radio" :class="{ active: statsDialogData.gubun === 'IN' }">
                <input type="radio" v-model="statsDialogData.gubun" value="IN" :disabled="statsDialogMode === 'edit'" />
                <span>수입</span>
              </label>
              <label class="gubun-radio" :class="{ active: statsDialogData.gubun === 'OUT' }">
                <input type="radio" v-model="statsDialogData.gubun" value="OUT" :disabled="statsDialogMode === 'edit'" />
                <span>지출</span>
              </label>
            </div>
          </div>
          <div class="form-group">
            <label>계정</label>
            <v-autocomplete
              v-model="statsDialogData.scode"
              :items="statsDialogData.gubun === 'IN' ? incomeCodeList : expenseCodeList"
              item-title="fc_sname"
              item-value="fc_scode"
              density="compact"
              variant="outlined"
              hide-details
              placeholder="계정 선택"
              :disabled="statsDialogMode === 'edit'"
            />
          </div>
          <div class="form-row">
            <div class="form-group half">
              <label>최소 금액</label>
              <input 
                type="number" 
                v-model.number="statsDialogData.minMoney"
                min="0"
                placeholder="0"
              />
            </div>
            <div class="form-group half">
              <label>최대 금액</label>
              <input 
                type="number" 
                v-model.number="statsDialogData.maxMoney"
                min="0"
                placeholder="0"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group half">
              <label>평균 금액</label>
              <input 
                type="number" 
                v-model.number="statsDialogData.avgMoney"
                min="0"
                placeholder="0"
              />
            </div>
            <div class="form-group half">
              <label>사용 횟수</label>
              <input 
                type="number" 
                v-model.number="statsDialogData.count"
                min="1"
                placeholder="1"
              />
            </div>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="btn-cancel" @click="statsDialogOpen = false">취소</button>
          <button 
            class="btn-save" 
            @click="saveStats" 
            :disabled="!statsDialogData.scode || !statsDialogData.gubun"
          >
            {{ statsDialogMode === 'add' ? '추가' : '수정' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- 확정하기 다이얼로그 -->
    <v-dialog v-model="confirmDialogOpen" max-width="520" persistent>
      <div class="delete-modal">
        <div class="delete-modal-header" style="background: linear-gradient(135deg, #059669 0%, #047857 100%);">
          <div class="delete-icon" style="background: rgba(255,255,255,0.2);">
            <v-icon size="28" color="white">mdi-lock-check</v-icon>
          </div>
          <h3>데이터 확정</h3>
          <p>확정 후에는 수정/삭제가 불가합니다</p>
        </div>
        <div class="delete-modal-body">
          <!-- 상대계정 미입력이 있는 경우 -->
          <template v-if="missingRelAccCount > 0">
            <p class="delete-message">
              상대계정이 없는 <strong class="count">{{ missingRelAccCount }}건</strong>을 자동으로 채운 후 확정합니다.
            </p>
            <div class="delete-summary">
              <div class="summary-row">
                <span class="label">수입 계정 수</span>
                <span class="value income">{{ availableIncomeAccounts.length }}개</span>
              </div>
              <div class="summary-row">
                <span class="label">총 수입 잔액</span>
                <span class="value income">{{ formatNumber(totalAvailableBalance) }}원</span>
              </div>
              <div class="summary-row">
                <span class="label">채울 지출 금액</span>
                <span class="value expense">{{ formatNumber(totalMissingRelAccAmount) }}원</span>
              </div>
              <div class="summary-row total" v-if="totalAvailableBalance < totalMissingRelAccAmount">
                <span class="label" style="color: #f59e0b;">⚠️ 잔액 부족</span>
                <span class="value" style="color: #f59e0b;">{{ formatNumber(totalMissingRelAccAmount - totalAvailableBalance) }}원</span>
              </div>
            </div>
            <div class="warning-box" style="background: #eff6ff; border-color: #3b82f6;">
              <v-icon size="16" color="#3b82f6">mdi-information</v-icon>
              <span>통장에 기록된 순서(등록순)대로 수입 잔액에서 차감합니다.</span>
            </div>
          </template>
          <!-- 상대계정이 모두 입력된 경우 -->
          <template v-else>
            <p class="delete-message">
              총 <strong class="count">{{ inoutList.length }}건</strong>의 현금출납 데이터를 확정합니다.
            </p>
            <div class="warning-box" style="background: #fef3c7; border-color: #f59e0b;">
              <v-icon size="16" color="#f59e0b">mdi-alert</v-icon>
              <span>확정된 데이터는 수정하거나 삭제할 수 없습니다.</span>
            </div>
          </template>
        </div>
        <div class="delete-modal-footer">
          <button type="button" class="btn-cancel" @click="confirmDialogOpen = false">취소</button>
          <button 
            type="button" 
            class="btn-delete" 
            style="background: linear-gradient(135deg, #059669 0%, #047857 100%);"
            @click="executeConfirm"
            :disabled="isConfirming"
          >
            <v-progress-circular v-if="isConfirming" indeterminate size="18" width="2" color="white" />
            <span v-else>
              <template v-if="missingRelAccCount > 0">{{ missingRelAccCount }}건 채우고 확정</template>
              <template v-else>확정하기</template>
            </span>
          </button>
        </div>
      </div>
    </v-dialog>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, reactive } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import PgmInoutAuto from '@/components/fna/PgmInoutAuto.vue';

const store = useStore();

// ============================================
// 인터페이스 정의
// ============================================

interface InoutData {
  hi_idx?: number;
  m_sisul_code?: string;
  hi_gubun: string;
  hi_scode?: string;
  hi_memo: string;
  hi_price: number;
  hi_inout_date: string;
  hi_funding?: string;
  hi_need_check?: string;
  hi_rel_acc?: string;
  hi_wdate?: string;
  hi_rdate?: string;
  hi_isuse?: string;
}

interface FinanceCode {
  fc_idx: number;
  fc_trcode: string;
  fc_trname: string;
  fc_year: string;
  fc_lcode: string;
  fc_lname: string;
  fc_mcode: string;
  fc_mname: string;
  fc_scode: string;
  fc_sname: string;
  fc_funding: string;
}

interface BalanceSummary {
  scode: string;
  sname: string;
  display: string;
  totalIncome: number;      // 총 수입액
  usedAmount: number;       // 이미 사용된 금액 (지출에서 rel_acc로 연결된)
  availableBalance: number; // 사용 가능 잔액
}

// ============================================
// 자금원 코드 매핑
// ============================================

const FUNDING_MAP: Record<string, string> = {
  '04': '자부담',
  '05': '후원금',
  '06': '수익사업',
  '07': '보조금'
};

// ============================================
// 상태 정의
// ============================================

const isLoading = ref(true);
const isSaving = ref(false);
const isDeleting = ref(false);
const inoutList = ref<InoutData[]>([]);
const carryoverBalance = ref({ income: 0, expense: 0, balance: 0 });  // 누적 이월금
const highlightedIdxList = ref<number[]>([]);  // 하이라이트된 행 목록
const financeCodes = ref<FinanceCode[]>([]);
const financeCodesLoading = ref(false);

// 인라인 수정 관련 상태
const editingRowIdx = ref<number | null>(null);
const inlineEditData = ref<InoutData>({
  hi_gubun: '',
  hi_scode: '',
  hi_memo: '',
  hi_price: 0,
  hi_inout_date: '',
  hi_funding: '',
  hi_rel_acc: ''
});
const originalInlineData = ref<InoutData | null>(null);  // 수정 전 원본 데이터
const isInlineSaving = ref(false);
const showSaveToast = ref(false);

// 날짜 관련 (localStorage에서 복원, 없으면 현재 날짜)
const STORAGE_KEY = 'inout_current_ym';
const savedYM = localStorage.getItem(STORAGE_KEY);
const defaultYear = new Date().getFullYear().toString();
const defaultMonth = (new Date().getMonth() + 1).toString().padStart(2, '0');

const currentYear = ref(savedYM ? savedYM.split('-')[0] : defaultYear);
const currentMonth = ref(savedYM ? savedYM.split('-')[1] : defaultMonth);

// 연월 변경 시 localStorage에 저장
watch([currentYear, currentMonth], ([year, month]) => {
  localStorage.setItem(STORAGE_KEY, `${year}-${month}`);
}, { immediate: true });

// 사이드바 위치 (기본: 오른쪽)
const sidebarPosition = ref<'left' | 'right'>('right');

// 월 선택기
const monthPickerRef = ref<HTMLInputElement | null>(null);

// 필터
const filterGubun = ref('all');
const filterNeedCheck = ref(false);
const searchScode = ref('');
const searchMemo = ref('');
const searchPrice = ref('');

// 정렬 (기본: 통장순 - hi_idx 오름차순)
const sortKey = ref<string>('hi_idx');
const sortOrder = ref<'asc' | 'desc'>('asc');

// 모두삭제 다이얼로그
const deleteAllDialogOpen = ref(false);

// 확정하기
const confirmDialogOpen = ref(false);
const isConfirming = ref(false);
const isFillingRelAcc = ref(false);

// 다이얼로그
const deleteDialogOpen = ref(false);
const autoDialogOpen = ref(false);

// 관리자 권한 확인 (시스템 관리자 또는 센터접속 중)
const isAdmin = computed(() => store.getters.isAdmin);
const isImpersonating = computed(() => store.getters.isImpersonating);
const canManageLearning = computed(() => isAdmin.value || isImpersonating.value);

// AI 학습 관리 모달
const dictionaryManageModalOpen = ref(false);
const dictionaryStats = ref<Record<string, { label: string; count: number }> | null>(null);
const selectedDictTable = ref<string | null>(null);
interface DictDetailItem {
  idx?: number;
  keyword: string;
  scode: string;
  scodeName?: string;
  count: number;
  relAcc?: string;
  relAccName?: string;
  funding?: string;
  avgAmount?: number;
  minMoney?: number;
  maxMoney?: number;
  gubun?: string;
  sisulCode?: string;
}
const dictDetailList = ref<DictDetailItem[]>([]);

// 계정별 그룹화된 딕셔너리 목록
interface DictKeyword {
  idx: number;
  keyword: string;
  count: number;
  relAcc?: string;
  relAccName?: string;
}
interface DictGroupedItem {
  scode: string;
  scodeName: string;
  funding?: string;
  relAcc?: string;
  relAccName?: string;
  keywords: DictKeyword[];
  totalCount: number;
}
const dictGroupedList = ref<DictGroupedItem[]>([]);

// 딕셔너리 검색
const dictSearchQuery = ref('');

// 검색어로 필터링된 딕셔너리 목록
const filteredDictGroupedList = computed(() => {
  if (!dictSearchQuery.value.trim()) {
    return dictGroupedList.value;
  }
  
  const query = dictSearchQuery.value.toLowerCase().trim();
  
  return dictGroupedList.value.filter(group => {
    // 계정명 또는 계정코드에서 검색
    const matchAccount = 
      (group.scodeName && group.scodeName.toLowerCase().includes(query)) ||
      (group.scode && group.scode.toLowerCase().includes(query));
    
    // 키워드에서 검색
    const matchKeyword = group.keywords.some(kw => 
      kw.keyword && kw.keyword.toLowerCase().includes(query)
    );
    
    return matchAccount || matchKeyword;
  });
});

// 키워드 추가/수정 다이얼로그
const keywordDialogOpen = ref(false);
const keywordDialogMode = ref<'add' | 'edit'>('add');
const keywordDialogData = ref({
  idx: 0,
  scode: '',
  scodeName: '',
  keyword: '',
  relAcc: '',
  funding: '',
  isNewAccount: false
});
const isDictDetailLoading = ref(false);

// 상대계정 매핑 추가/수정 다이얼로그
const relmapDialogOpen = ref(false);
const relmapDialogMode = ref<'add' | 'edit'>('add');
const relmapDialogData = ref({
  idx: 0,
  scode: '',
  relAcc: '',
  count: 1
});

// 계정별 통계 추가/수정 다이얼로그
const statsDialogOpen = ref(false);
const statsDialogMode = ref<'add' | 'edit'>('add');
const statsDialogData = ref({
  idx: 0,
  gubun: 'OUT' as 'IN' | 'OUT',
  scode: '',
  minMoney: 0,
  maxMoney: 0,
  avgMoney: 0,
  count: 1
});

const selectedItem = ref<InoutData | null>(null);

// 학습관리 모달 관련
const learningModalOpen = ref(false);
const learningItem = ref<InoutData | null>(null);
const isSavingLearning = ref(false);
const learningFormData = ref({
  scode: '',
  funding: '',
  relAcc: ''
});

// 유사 항목 적용 확인 모달
const applySimilarConfirmOpen = ref(false);
const similarItemsCount = ref(0);
const pendingSaveData = ref<{
  sisulCode: string;
  gubun: string;
  memo: string;
  scode: string;
  funding: string;
  relAcc: string;
  excludeIdx: number;
  price: number;
} | null>(null);

interface NeedCheckInfo {
  status?: 'OK' | 'NEED_CHECK';
  type: string;
  reason?: string;
  reviewReason?: string;  // ★ 확인필요 이유 (학습 미완료/중복 계정)
  confidence?: string;
  scode?: string;
  funding?: string;
  rel_acc?: string;
  keywords?: string[];
  prediction?: {
    scode: string;
    funding: string;
    rel_acc?: string;
    probability: number;
    source: string;
    matchedKeywords: number;
    totalCount: number;
    keywords?: string[];
    minMoney?: number;
    maxMoney?: number;
    avgMoney?: number;
    moneyMatch?: number;
    moneyMatchText?: string;
    inputPrice?: number;
    relAccDistribution?: Array<{ rel_acc: string; sname: string; count: number; percentage: number }>;
    relAccReason?: string;
  };
  alternatives?: Array<{
    scode: string;
    rel_acc?: string;
    funding?: string;
    probability: number;
    count?: number;
    moneyMatch?: number;
  }>;
  selected_rel_acc?: string;
}

const parsedNeedCheck = computed<NeedCheckInfo | null>(() => {
  if (!learningItem.value?.hi_need_check) return null;
  try {
    return JSON.parse(learningItem.value.hi_need_check);
  } catch {
    return null;
  }
});

// ============================================
// 잔액 계산기 (BalanceCalculator)
// ============================================

const useBalanceCalculator = () => {
  /**
   * 수입 데이터를 scode별로 집계
   */
  const aggregateIncomeByCode = (data: InoutData[]): Map<string, number> => {
    return data
      .filter(item => item.hi_gubun === 'IN' && item.hi_scode)
      .reduce((acc, item) => {
        const scode = item.hi_scode!;
        const currentAmount = acc.get(scode) || 0;
        acc.set(scode, currentAmount + (item.hi_price || 0));
        return acc;
      }, new Map<string, number>());
  };

  /**
   * 지출 데이터에서 상대계정(rel_acc)별 사용 금액 집계
   * @param excludeIdx 제외할 항목 ID (수정 모드에서 현재 항목 제외)
   */
  const aggregateExpenseByRelAcc = (data: InoutData[], excludeIdx?: number): Map<string, number> => {
    return data
      .filter(item => {
        if (item.hi_gubun !== 'OUT' || !item.hi_rel_acc) return false;
        if (excludeIdx && item.hi_idx === excludeIdx) return false;
        return true;
      })
      .reduce((acc, item) => {
        const relAcc = item.hi_rel_acc!;
        const currentAmount = acc.get(relAcc) || 0;
        acc.set(relAcc, currentAmount + (item.hi_price || 0));
        return acc;
      }, new Map<string, number>());
  };

  /**
   * 수입코드별 잔액 정보 계산
   */
  const calculateBalanceSummaries = (
    incomeMap: Map<string, number>,
    expenseMap: Map<string, number>,
    codes: FinanceCode[]
  ): BalanceSummary[] => {
    const summaries: BalanceSummary[] = [];

    incomeMap.forEach((totalIncome, scode) => {
      const usedAmount = expenseMap.get(scode) || 0;
      const availableBalance = totalIncome - usedAmount;

      // 해당 scode의 이름 찾기
      const financeCode = codes.find(
        fc => fc.fc_trcode === 'IN' && fc.fc_scode === scode
      );
      const sname = financeCode?.fc_sname || scode;

      summaries.push({
        scode,
        sname,
        display: sname,
        totalIncome,
        usedAmount,
        availableBalance
      });
    });

    // 잔액 내림차순 정렬
    return summaries.sort((a, b) => b.availableBalance - a.availableBalance);
  };

  return {
    aggregateIncomeByCode,
    aggregateExpenseByRelAcc,
    calculateBalanceSummaries
  };
};

const balanceCalculator = useBalanceCalculator();

// ============================================
// Computed 속성
// ============================================

// 수입 계정코드 리스트
const incomeCodeList = computed(() => {
  return financeCodes.value.filter(fc => fc.fc_trcode === 'IN');
});

// 지출 계정코드 리스트
const expenseCodeList = computed(() => {
  return financeCodes.value.filter(fc => fc.fc_trcode === 'OUT');
});

// 학습 모달용 상대계정 옵션 (현재 등록된 수입 항목에서만 선택 가능)
const learningAvailableRelAcc = computed((): BalanceSummary[] => {
  if (!learningItem.value || learningItem.value.hi_gubun !== 'OUT') return [];
  
  // 수입 집계
  const incomeMap = balanceCalculator.aggregateIncomeByCode(inoutList.value);
  
  // 지출(상대계정) 집계 - 현재 학습 항목 제외
  const excludeIdx = learningItem.value.hi_idx;
  const expenseMap = balanceCalculator.aggregateExpenseByRelAcc(inoutList.value, excludeIdx);
  
  // 잔액 정보 계산
  return balanceCalculator.calculateBalanceSummaries(incomeMap, expenseMap, financeCodes.value);
});

// 필터링된 리스트
const filteredInoutList = computed(() => {
  // 필터링
  let result = inoutList.value.filter(item => {
    // 구분 필터
    if (filterGubun.value !== 'all' && item.hi_gubun !== filterGubun.value) {
      return false;
    }
    // 확인필요 필터: 필수 항목이 없거나 need_check가 있는 항목만 표시
    if (filterNeedCheck.value) {
      if (!needsReview(item)) {
      return false;
    }
    }
    // 계정 검색
    if (searchScode.value) {
      const keyword = searchScode.value.toLowerCase();
      const scodeName = getScodeDisplayName(item.hi_scode, item.hi_gubun);
      const relAccName = item.hi_rel_acc ? getScodeDisplayName(item.hi_rel_acc, 'IN') : '';
      const scodeFields = [item.hi_scode, scodeName, item.hi_rel_acc, relAccName];
      if (!scodeFields.some(field => field?.toLowerCase().includes(keyword))) {
        return false;
      }
    }
    // 적요 검색
    if (searchMemo.value) {
      const keyword = searchMemo.value.toLowerCase();
      if (!item.hi_memo?.toLowerCase().includes(keyword)) {
        return false;
      }
    }
    // 금액 검색
    if (searchPrice.value) {
      const keyword = searchPrice.value.replace(/,/g, '');
      const priceStr = item.hi_price?.toString() || '';
      if (!priceStr.includes(keyword)) {
        return false;
      }
    }
    return true;
  });
  
  // 정렬: 전년도이월금 우선, 그 다음 날짜순 (통장내역 순서)
  result = [...result].sort((a, b) => {
    // 전년도이월금은 항상 맨 위
    const aIsCarryover = a.hi_memo?.includes('전년도이월금') || a.hi_memo?.includes('이월금');
    const bIsCarryover = b.hi_memo?.includes('전년도이월금') || b.hi_memo?.includes('이월금');
    
    if (aIsCarryover && !bIsCarryover) return -1;
    if (!aIsCarryover && bIsCarryover) return 1;
    
    // 사용자 정의 정렬
    let aVal: any = a[sortKey.value as keyof InoutData];
    let bVal: any = b[sortKey.value as keyof InoutData];
    
    // null 처리
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    
    // 숫자 비교
    if (sortKey.value === 'hi_price') {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    }
    
    // 비교
    if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1;
    return 0;
  });
  
  return result;
});

// 검색 필터 전체 초기화
const clearAllSearch = () => {
  searchScode.value = '';
  searchMemo.value = '';
  searchPrice.value = '';
};

// 확인 필요 여부 판단 (reviewReason 기준)
// - 학습되지 않은 적요 (패턴 기반 추론)
// - 여러 계정이 비슷한 확률로 추론된 경우
const needsReview = (item: InoutData): boolean => {
  // hi_need_check 파싱하여 reviewReason 확인
  if (item.hi_need_check && item.hi_need_check.trim() !== '') {
    try {
      const parsed = JSON.parse(item.hi_need_check);
      // ★ reviewReason이 있으면 확인 필요 (학습 미완료 또는 중복 계정)
      if (parsed.reviewReason) {
        return true;
      }
      // reviewReason이 없으면 OK로 간주
      return false;
    } catch {
      // JSON 파싱 실패 시 확인 필요로 간주
      return true;
    }
  }
  
  // hi_need_check가 없으면 확인 필요 아님
  return false;
};

// 상대계정 없는 지출 여부 (별도 경고용)
const isMissingRelAcc = (item: InoutData): boolean => {
  if (item.hi_gubun !== 'OUT') return false;
  return !item.hi_rel_acc || item.hi_rel_acc.trim() === '';
};

// 확인필요 이유 추출
const getReviewReason = (item: InoutData): string | null => {
  if (!item.hi_need_check) return null;
  try {
    const parsed = JSON.parse(item.hi_need_check);
    return parsed.reviewReason || null;
  } catch {
    return null;
  }
};

// 확인 필요 항목 개수
const needReviewCount = computed(() => {
  return inoutList.value.filter(item => needsReview(item)).length;
});

// 필터로 인해 데이터가 비어있을 때 메시지
const getFilteredEmptyMessage = computed(() => {
  const filters = [];
  
  if (filterNeedCheck.value) {
    filters.push(`'확인필요' 필터`);
  }
  if (filterGubun.value === 'IN') {
    filters.push(`'수입만' 필터`);
  } else if (filterGubun.value === 'OUT') {
    filters.push(`'지출만' 필터`);
  }
  if (searchScode.value) {
    filters.push(`계정코드 검색`);
  }
  if (searchMemo.value) {
    filters.push(`적요 검색`);
  }
  if (searchPrice.value) {
    filters.push(`금액 검색`);
  }
  
  if (filters.length > 0) {
    return `${filters.join(', ')}에 해당하는 항목이 없습니다.`;
  }
  return '필터 조건에 맞는 항목이 없습니다.';
});

// 계정이 없는 항목 개수 (수입/지출 모두)
const missingScodeCount = computed(() => {
  return inoutList.value.filter(item => 
    !item.hi_scode || item.hi_scode.trim() === ''
  ).length;
});

// 모든 항목에 계정이 채워져 있는지 여부
const allScodesFilled = computed(() => missingScodeCount.value === 0);

// 상대계정이 없는 지출 항목 개수
const missingRelAccCount = computed(() => {
  return inoutList.value.filter(item => 
    item.hi_gubun === 'OUT' && 
    item.hi_scode && // 계정코드는 있어야 함
    (!item.hi_rel_acc || item.hi_rel_acc.trim() === '')
  ).length;
});

// 상대계정이 없는 지출 항목 목록 (hi_idx 순)
const missingRelAccItems = computed(() => {
  return inoutList.value
    .filter(item => 
      item.hi_gubun === 'OUT' && 
      item.hi_scode && 
      (!item.hi_rel_acc || item.hi_rel_acc.trim() === '')
    )
    .sort((a, b) => (a.hi_idx || 0) - (b.hi_idx || 0));
});

// 상대계정 없는 지출 총액
const totalMissingRelAccAmount = computed(() => {
  return missingRelAccItems.value.reduce((sum, item) => sum + (item.hi_price || 0), 0);
});

// 사용 가능한 수입 계정 (잔액 계산 포함, hi_idx 순)
const availableIncomeAccounts = computed(() => {
  // 수입 항목을 hi_idx 순으로 정렬
  const incomeItems = inoutList.value
    .filter(item => item.hi_gubun === 'IN' && item.hi_scode)
    .sort((a, b) => (a.hi_idx || 0) - (b.hi_idx || 0));
  
  // scode별 수입 집계
  const incomeByCode: Record<string, { totalIncome: number; sname: string; funding: string | undefined; firstIdx: number }> = {};
  incomeItems.forEach(item => {
    const scode = item.hi_scode!;
    if (!incomeByCode[scode]) {
      incomeByCode[scode] = {
        totalIncome: 0,
        sname: getScodeDisplayName(scode, 'IN'),
        funding: item.hi_funding,
        firstIdx: item.hi_idx || 0
      };
    }
    incomeByCode[scode].totalIncome += item.hi_price || 0;
    // funding이 없으면 업데이트
    if (!incomeByCode[scode].funding && item.hi_funding) {
      incomeByCode[scode].funding = item.hi_funding;
    }
  });
  
  // 이미 사용된 금액 차감 (상대계정이 있는 지출)
  inoutList.value
    .filter(item => item.hi_gubun === 'OUT' && item.hi_rel_acc)
    .forEach(item => {
      const relAcc = item.hi_rel_acc!;
      if (incomeByCode[relAcc]) {
        incomeByCode[relAcc].totalIncome -= item.hi_price || 0;
      }
    });
  
  // 잔액이 있는 것만 반환 (firstIdx 순 정렬)
  return Object.entries(incomeByCode)
    .filter(([, data]) => data.totalIncome > 0)
    .map(([scode, data]) => ({
      scode,
      sname: data.sname,
      funding: data.funding,
      availableBalance: data.totalIncome,
      firstIdx: data.firstIdx
    }))
    .sort((a, b) => a.firstIdx - b.firstIdx);
});

// 총 사용 가능 잔액
const totalAvailableBalance = computed(() => {
  return availableIncomeAccounts.value.reduce((sum, acc) => sum + acc.availableBalance, 0);
});

// 상대계정 선택용 옵션 (잔여금액 포함, 잔액 있는 것 우선 정렬)
const relAccOptionsWithBalance = computed(() => {
  return availableIncomeAccounts.value
    .map(acc => ({
      scode: acc.scode,
      sname: acc.sname,
      availableBalance: acc.availableBalance,
      displayName: `${acc.sname} (${formatNumber(acc.availableBalance)}원)`
    }))
    .sort((a, b) => b.availableBalance - a.availableBalance); // 잔액 높은 순
});

// 통장순 정렬로 설정 (INSERT 순서 = hi_idx 오름차순)
const setSortToChronological = () => {
  sortKey.value = 'hi_idx';
  sortOrder.value = 'asc';
};

// 정렬 토글
const toggleSort = (key: string) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortOrder.value = 'desc';
  }
};

// 정렬 아이콘
const getSortIcon = (key: string) => {
  if (sortKey.value !== key) return 'mdi-unfold-more-horizontal';
  return sortOrder.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down';
};

// 필터 초기화
const resetFilters = () => {
  filterGubun.value = 'all';
  filterNeedCheck.value = false;
  searchScode.value = '';
  searchMemo.value = '';
  searchPrice.value = '';
};

// ============================================
// AI 학습 관리 기능
// ============================================

// 학습 데이터 통계 조회
const fetchDictionaryStats = async () => {
  try {
    const response = await axios.get('/api/fna/inout/dictionary/stats');
    if (response.data.success) {
      dictionaryStats.value = response.data.data;
    }
  } catch (error) {
    console.error('학습 통계 조회 오류:', error);
    dictionaryStats.value = null;
  }
};

// 학습 관리 모달 열기
const openDictionaryManageModal = async () => {
  dictionaryManageModalOpen.value = true;
  selectedDictTable.value = null;
  dictDetailList.value = [];
  await fetchDictionaryStats();
};

// 딕셔너리 테이블 선택
const selectDictTable = async (tableKey: string) => {
  if (selectedDictTable.value === tableKey) {
    selectedDictTable.value = null;
    dictDetailList.value = [];
    dictGroupedList.value = [];
    return;
  }
  
  selectedDictTable.value = tableKey;
  isDictDetailLoading.value = true;
  dictDetailList.value = [];
  dictGroupedList.value = [];
  dictSearchQuery.value = '';  // 테이블 변경 시에만 검색어 초기화
  
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    
    // 수입/지출 딕셔너리는 그룹화된 데이터 조회
    if (tableKey === 'income' || tableKey === 'expense') {
      const response = await axios.get(`/api/fna/inout/dictionary/grouped/${tableKey}`, {
        params: { sisulCode }
      });
      
      if (response.data.success) {
        dictGroupedList.value = response.data.data || [];
      }
    } else {
      // 계정관계맵, 통계는 기존 방식
      const response = await axios.get(`/api/fna/inout/dictionary/detail/${tableKey}`, {
        params: { sisulCode }
      });
      
      if (response.data.success) {
        dictDetailList.value = response.data.data || [];
      }
    }
  } catch (error) {
    console.error('딕셔너리 상세 조회 오류:', error);
    dictDetailList.value = [];
    dictGroupedList.value = [];
  } finally {
    isDictDetailLoading.value = false;
  }
};

// 딕셔너리 데이터 새로고침 (검색어 유지)
const refreshDictData = async () => {
  const tableKey = selectedDictTable.value;
  if (!tableKey) return;
  
  isDictDetailLoading.value = true;
  
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    
    if (tableKey === 'income' || tableKey === 'expense') {
      const response = await axios.get(`/api/fna/inout/dictionary/grouped/${tableKey}`, {
        params: { sisulCode }
      });
      
      if (response.data.success) {
        dictGroupedList.value = response.data.data || [];
      }
    } else {
      const response = await axios.get(`/api/fna/inout/dictionary/detail/${tableKey}`, {
        params: { sisulCode }
      });
      
      if (response.data.success) {
        dictDetailList.value = response.data.data || [];
      }
    }
  } catch (error) {
    console.error('딕셔너리 새로고침 오류:', error);
  } finally {
    isDictDetailLoading.value = false;
  }
};

// 딕셔너리 테이블 라벨 가져오기
const getDictTableLabel = (tableKey: string): string => {
  const labels: Record<string, string> = {
    income: '수입 딕셔너리',
    expense: '지출 딕셔너리',
    scodeRelmap: '계정관계 맵',
    scodeStats: '계정 통계'
  };
  return labels[tableKey] || tableKey;
};

// 새 계정 추가 다이얼로그 열기
const openNewAccountDialog = () => {
  keywordDialogMode.value = 'add';
  keywordDialogData.value = {
    idx: 0,
    scode: '',
    scodeName: '',
    keyword: '',
    relAcc: '',
    funding: '',
    isNewAccount: true
  };
  keywordDialogOpen.value = true;
};

// 계정 선택 시 이름 업데이트
const onAccountSelect = (scode: string) => {
  if (!scode) {
    keywordDialogData.value.scodeName = '';
    return;
  }
  const codeList = selectedDictTable.value === 'income' ? incomeCodeList.value : expenseCodeList.value;
  const found = codeList.find((c: any) => c.fc_scode === scode);
  keywordDialogData.value.scodeName = found?.fc_sname || '';
};

// 키워드 추가 다이얼로그 열기 (기존 계정에)
const openAddKeywordDialog = (group: DictGroupedItem) => {
  keywordDialogMode.value = 'add';
  keywordDialogData.value = {
    idx: 0,
    scode: group.scode,
    scodeName: group.scodeName,
    keyword: '',
    relAcc: group.relAcc || '',
    funding: group.funding || '',
    isNewAccount: false
  };
  keywordDialogOpen.value = true;
};

// 키워드 수정 다이얼로그 열기
const openEditKeywordDialog = (kw: DictKeyword, group: DictGroupedItem) => {
  keywordDialogMode.value = 'edit';
  keywordDialogData.value = {
    idx: kw.idx,
    scode: group.scode,
    scodeName: group.scodeName,
    keyword: kw.keyword,
    relAcc: kw.relAcc || group.relAcc || '',
    funding: group.funding || '',
    isNewAccount: false
  };
  keywordDialogOpen.value = true;
};

// 키워드 저장 (추가/수정)
const saveKeyword = async () => {
  if (!keywordDialogData.value.keyword?.trim()) return;
  
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    
    if (keywordDialogMode.value === 'add') {
      // 키워드 추가
      await axios.post('/api/fna/inout/dictionary/keyword', {
        tableKey: selectedDictTable.value,
        sisulCode,
        scode: keywordDialogData.value.scode,
        keyword: keywordDialogData.value.keyword.trim(),
        relAcc: keywordDialogData.value.relAcc,
        funding: keywordDialogData.value.funding
      });
    } else {
      // 키워드 수정
      await axios.put(`/api/fna/inout/dictionary/keyword/${keywordDialogData.value.idx}`, {
        tableKey: selectedDictTable.value,
        keyword: keywordDialogData.value.keyword.trim()
      });
    }
    
    keywordDialogOpen.value = false;
    
    // 데이터 새로고침
    if (selectedDictTable.value) {
      await selectDictTable(selectedDictTable.value);
      // 선택 상태 유지 위해 다시 선택
      selectedDictTable.value = selectedDictTable.value;
    }
    
    // 통계도 갱신
    await fetchDictionaryStats();
    
  } catch (error) {
    console.error('키워드 저장 오류:', error);
    alert('키워드 저장 중 오류가 발생했습니다.');
  }
};

// 키워드 삭제
const deleteKeyword = async (kw: DictKeyword) => {
  if (!confirm(`"${kw.keyword}" 키워드를 삭제하시겠습니까?`)) return;
  
  try {
    const tableKey = selectedDictTable.value;
    await axios.delete(`/api/fna/inout/dictionary/item/${tableKey}/${kw.idx}`);
    
    // 데이터 새로고침 (검색어 유지)
    await refreshDictData();
    
    // 통계 갱신
    await fetchDictionaryStats();
    
  } catch (error) {
    console.error('키워드 삭제 오류:', error);
    alert('키워드 삭제 중 오류가 발생했습니다.');
  }
};

// 상대계정 매핑 다이얼로그 열기
const openRelmapDialog = (mode: 'add' | 'edit', item?: DictDetailItem) => {
  relmapDialogMode.value = mode;
  if (mode === 'edit' && item) {
    relmapDialogData.value = {
      idx: item.idx || 0,
      scode: item.scode || '',
      relAcc: item.relAcc || '',
      count: item.count || 1
    };
  } else {
    relmapDialogData.value = {
      idx: 0,
      scode: '',
      relAcc: '',
      count: 1
    };
  }
  relmapDialogOpen.value = true;
};

// 상대계정 매핑 저장
const saveRelmap = async () => {
  if (!relmapDialogData.value.scode || !relmapDialogData.value.relAcc) return;
  
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    
    if (relmapDialogMode.value === 'add') {
      await axios.post('/api/fna/inout/dictionary/scodeRelmap', {
        sisulCode,
        scode: relmapDialogData.value.scode,
        relAcc: relmapDialogData.value.relAcc,
        count: relmapDialogData.value.count || 1
      });
    } else {
      await axios.put(`/api/fna/inout/dictionary/scodeRelmap/${relmapDialogData.value.idx}`, {
        scode: relmapDialogData.value.scode,
        relAcc: relmapDialogData.value.relAcc,
        count: relmapDialogData.value.count || 1
      });
    }
    
    relmapDialogOpen.value = false;
    
    // 데이터 새로고침 (검색어 유지)
    await refreshDictData();
    
    // 통계 갱신
    await fetchDictionaryStats();
    
  } catch (error) {
    console.error('상대계정 매핑 저장 오류:', error);
    alert('저장 중 오류가 발생했습니다.');
  }
};

// 계정별 통계 다이얼로그 열기
const openStatsDialog = (mode: 'add' | 'edit', item?: DictDetailItem) => {
  statsDialogMode.value = mode;
  if (mode === 'edit' && item) {
    statsDialogData.value = {
      idx: item.idx || 0,
      gubun: (item.gubun as 'IN' | 'OUT') || 'OUT',
      scode: item.scode || '',
      minMoney: item.minMoney || 0,
      maxMoney: item.maxMoney || 0,
      avgMoney: item.avgAmount || 0,
      count: item.count || 1
    };
  } else {
    statsDialogData.value = {
      idx: 0,
      gubun: 'OUT',
      scode: '',
      minMoney: 0,
      maxMoney: 0,
      avgMoney: 0,
      count: 1
    };
  }
  statsDialogOpen.value = true;
};

// 계정별 통계 저장
const saveStats = async () => {
  if (!statsDialogData.value.scode || !statsDialogData.value.gubun) return;
  
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    
    if (statsDialogMode.value === 'add') {
      await axios.post('/api/fna/inout/dictionary/scodeStats', {
        sisulCode,
        gubun: statsDialogData.value.gubun,
        scode: statsDialogData.value.scode,
        minMoney: statsDialogData.value.minMoney || 0,
        maxMoney: statsDialogData.value.maxMoney || 0,
        avgMoney: statsDialogData.value.avgMoney || 0,
        count: statsDialogData.value.count || 1
      });
    } else {
      await axios.put(`/api/fna/inout/dictionary/scodeStats/${statsDialogData.value.idx}`, {
        scode: statsDialogData.value.scode,
        minMoney: statsDialogData.value.minMoney || 0,
        maxMoney: statsDialogData.value.maxMoney || 0,
        avgMoney: statsDialogData.value.avgMoney || 0,
        count: statsDialogData.value.count || 1
      });
    }
    
    statsDialogOpen.value = false;
    
    // 데이터 새로고침 (검색어 유지)
    await refreshDictData();
    
    // 통계 갱신
    await fetchDictionaryStats();
    
  } catch (error) {
    console.error('계정별 통계 저장 오류:', error);
    alert('저장 중 오류가 발생했습니다.');
  }
};

// 딕셔너리 항목 삭제
const deleteDictItem = async (item: DictDetailItem) => {
  if (!selectedDictTable.value || !item.idx) {
    alert('삭제할 수 없는 항목입니다.');
    return;
  }
  
  // 항목 유형에 따른 삭제 확인 메시지
  let confirmMsg = '';
  if (selectedDictTable.value === 'scodeRelmap') {
    confirmMsg = `"${item.scodeName || item.scode}" → "${item.relAccName || item.relAcc}" 매핑을 삭제하시겠습니까?`;
  } else if (selectedDictTable.value === 'scodeStats') {
    confirmMsg = `"${item.scodeName || item.scode}" 계정 통계를 삭제하시겠습니까?`;
  } else {
    confirmMsg = `"${item.keyword}" 학습 데이터를 삭제하시겠습니까?`;
  }
  confirmMsg += '\n이 작업은 되돌릴 수 없습니다.';
  
  if (!confirm(confirmMsg)) {
    return;
  }
  
  try {
    const response = await axios.delete(`/api/fna/inout/dictionary/item/${selectedDictTable.value}/${item.idx}`);
    
    if (response.data.success) {
      // 목록에서 제거
      dictDetailList.value = dictDetailList.value.filter(d => d.idx !== item.idx);
      
      // 통계 갱신
      await fetchDictionaryStats();
    } else {
      alert('삭제 실패: ' + response.data.message);
    }
  } catch (error) {
    console.error('딕셔너리 삭제 오류:', error);
    alert('삭제 중 오류가 발생했습니다.');
  }
};

// 요약 데이터
const summaryData = computed(() => {
  const { income, expense } = inoutList.value.reduce(
    (acc, item) => {
      if (item.hi_gubun === 'IN') {
        acc.income += item.hi_price || 0;
      } else {
        acc.expense += item.hi_price || 0;
      }
      return acc;
    },
    { income: 0, expense: 0 }
  );

  return {
    income,
    expense,
    balance: income - expense
  };
});

// 이월금 (해당월 이전까지의 누적 잔액)
const prevMonthBalance = computed(() => {
  return carryoverBalance.value;
});

// 누적잔액 = 이월금 + 당월수입 - 당월지출
const totalBalance = computed(() => {
  return prevMonthBalance.value.balance + summaryData.value.income - summaryData.value.expense;
});

// ============================================
// 유틸리티 함수
// ============================================

const getFundingName = (code: string | undefined): string => {
  if (!code) return '-';
  return FUNDING_MAP[code] || code;
};

const getScodeDisplayName = (scode: string | undefined, gubun: string): string => {
  if (!scode) return '-';
  
  // 먼저 지정된 gubun으로 검색
  let financeCode = financeCodes.value.find(
    fc => fc.fc_trcode === gubun && fc.fc_scode === scode
  );
  
  // 못 찾으면 반대쪽도 검색 (상대계정은 수입 코드이므로)
  if (!financeCode) {
    financeCode = financeCodes.value.find(
      fc => fc.fc_scode === scode
    );
  }
  
  return financeCode ? financeCode.fc_sname : `[${scode}]`;
};

// 상대계정 분포에서 해당 상대계정의 퍼센트 가져오기 (실시간 분포 우선)
const getRelAccPercentage = (relAcc: string | undefined): number => {
  if (!relAcc) return 0;
  
  // 실시간 조회된 분포 우선 사용
  if (realTimeRelAccDistribution.value && realTimeRelAccDistribution.value.length > 0) {
    const dist = realTimeRelAccDistribution.value.find((d: any) => d.rel_acc === relAcc);
    return dist?.percentage || 0;
  }
  
  // 없으면 저장된 분포 사용
  const distribution = parsedNeedCheck.value?.prediction?.relAccDistribution;
  if (!distribution || !Array.isArray(distribution)) return 0;
  
  const dist = distribution.find((d: any) => d.rel_acc === relAcc);
  return dist?.percentage || 0;
};

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('ko-KR').format(num || 0);
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  return dateStr.replace(/-/g, '.');
};

// ============================================
// 금액 입력 핸들러
// ============================================

const handlePriceKeydown = (event: KeyboardEvent) => {
  // 허용 키: 숫자, 백스페이스, 삭제, 화살표, 탭
  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
  const isNumber = /^[0-9]$/.test(event.key);
  
  if (!isNumber && !allowedKeys.includes(event.key)) {
    event.preventDefault();
  }
};

// 인라인 금액 입력 키 핸들러 (Enter/Esc 지원)
const handleInlinePriceKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    saveInlineEdit();
    return;
  }
  if (event.key === 'Escape') {
    cancelInlineEdit();
    return;
  }
  
  // 기존 금액 입력 검증
  handlePriceKeydown(event);
};

// ============================================
// 인라인 수정 함수
// ============================================

// 행 클릭 시 인라인 수정 시작
const startInlineEdit = async (item: InoutData, event: Event) => {
  // 확정된 상태면 수정 불가
  if (isConfirmed.value) {
    return;
  }
  
  // 이미 수정 중인 같은 행이면 무시
  if (editingRowIdx.value === item.hi_idx) {
    return;
  }
  
  // 이미 수정 중인 다른 행이 있으면 취소 (저장하지 않음)
  if (editingRowIdx.value && editingRowIdx.value !== item.hi_idx) {
    cancelInlineEdit();
  }
  
  // 새 행 수정 시작
  editingRowIdx.value = item.hi_idx || null;
  inlineEditData.value = { ...item };
  originalInlineData.value = { ...item };
  
  // 학습 정보 조회
  if (item.hi_memo) {
    await fetchLearningInfoByMemo(item.hi_gubun, item.hi_memo);
  }
};

// 인라인 금액 입력 핸들러
const handleInlinePriceInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const rawValue = target.value.replace(/[^0-9]/g, '');
  const numericValue = rawValue ? parseInt(rawValue, 10) : 0;
  inlineEditData.value.hi_price = numericValue;
  target.value = numericValue ? numericValue.toLocaleString() : '';
};

// 인라인 blur 핸들러 (더 이상 자동 저장하지 않음 - 명시적인 저장 버튼 사용)
const handleInlineBlur = () => {
  // blur 시 자동 저장 비활성화 - 사용자가 저장/취소 버튼을 명시적으로 클릭해야 함
  // 다른 행 클릭 시 기존 수정 취소
};

// 인라인 수정 취소
const cancelInlineEdit = () => {
  editingRowIdx.value = null;
  inlineEditData.value = {
    hi_gubun: '',
    hi_scode: '',
    hi_memo: '',
    hi_price: 0,
    hi_inout_date: '',
    hi_funding: '',
    hi_rel_acc: ''
  };
  originalInlineData.value = null;
  inlineLearningInfo.value = [];
  inlineLearningKeywords.value = [];
  // 키워드 편집/추가 상태 초기화
  newKeywordInput.value = '';
  editingKeywordIdx.value = null;
  editingKeywordValue.value = '';
  editingKeywordScode.value = '';
};

// ============================================
// 인라인 수정 - 학습 정보 조회
// ============================================

interface InlineLearningItem {
  idx: number;
  sisulCode: string;
  keyword: string;
  scode: string;
  scodeName: string;
  funding?: string;
  relAcc?: string;
  relAccName?: string;
  count: number;
  minMoney: number;
  maxMoney: number;
  scope: string;
}

const inlineLearningInfo = ref<InlineLearningItem[]>([]);
const inlineLearningKeywords = ref<string[]>([]);
const isLoadingLearningInfo = ref(false);

// 적요에서 학습 정보 조회
const fetchLearningInfoByMemo = async (gubun: string, memo: string) => {
  if (!memo || memo.trim().length < 2) {
    inlineLearningInfo.value = [];
    inlineLearningKeywords.value = [];
    return;
  }
  
  isLoadingLearningInfo.value = true;
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    const response = await axios.post('/api/fna/inout/dictionary/search-by-memo', {
      sisulCode,
      gubun,
      memo
    });
    
    if (response.data.success) {
      inlineLearningInfo.value = response.data.data || [];
      inlineLearningKeywords.value = response.data.keywords || [];
    }
  } catch (error) {
    console.error('학습 정보 조회 오류:', error);
    inlineLearningInfo.value = [];
    inlineLearningKeywords.value = [];
  } finally {
    isLoadingLearningInfo.value = false;
  }
};

// 인라인 학습 데이터 삭제
const deleteInlineLearningItem = async (item: InlineLearningItem) => {
  if (!confirm(`"${item.keyword}" 키워드를 삭제하시겠습니까?`)) return;
  
  try {
    const tableKey = inlineEditData.value.hi_gubun === 'IN' ? 'income' : 'expense';
    await axios.delete(`/api/fna/inout/dictionary/item/${tableKey}/${item.idx}`);
    
    // 목록에서 제거
    inlineLearningInfo.value = inlineLearningInfo.value.filter(i => i.idx !== item.idx);
  } catch (error) {
    console.error('학습 데이터 삭제 오류:', error);
    alert('삭제 중 오류가 발생했습니다.');
  }
};

// 학습되지 않은 키워드 목록 (추출된 키워드 중 현재 계정에 매핑되지 않은 것)
const unlearnedKeywords = computed(() => {
  if (!inlineEditData.value.hi_scode) return [];
  
  // 이미 학습된 키워드 목록 (현재 계정에 매핑된 것)
  const learnedKeywordsForCurrentScode = new Set(
    inlineLearningInfo.value
      .filter(item => item.scode === inlineEditData.value.hi_scode)
      .map(item => item.keyword.toLowerCase())
  );
  
  // 추출된 키워드 중 아직 현재 계정에 학습되지 않은 것
  return inlineLearningKeywords.value.filter(
    kw => !learnedKeywordsForCurrentScode.has(kw.toLowerCase())
  );
});

// 키워드 학습 중 상태
const learningKeyword = ref<string | null>(null);

// 개별 키워드를 현재 계정에 학습시키기
const learnKeywordToCurrentAccount = async (keyword: string) => {
  if (!inlineEditData.value.hi_scode || !keyword) return;
  
  learningKeyword.value = keyword;
  
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    const tableKey = inlineEditData.value.hi_gubun === 'IN' ? 'income' : 'expense';
    
    await axios.post('/api/fna/inout/dictionary/keyword', {
      tableKey,
      sisulCode,
      scode: inlineEditData.value.hi_scode,
      keyword: keyword.trim(),
      relAcc: inlineEditData.value.hi_rel_acc || null,
      funding: inlineEditData.value.hi_funding || null
    });
    
    // 성공 시 학습 정보 목록에 추가 (UI 즉시 반영)
    const scodeName = getScodeDisplayName(inlineEditData.value.hi_scode, inlineEditData.value.hi_gubun);
    inlineLearningInfo.value.push({
      idx: Date.now(), // 임시 ID
      sisulCode: store.state.user?.m_sisul_code || '',
      keyword: keyword,
      scode: inlineEditData.value.hi_scode,
      scodeName: scodeName,
      relAcc: inlineEditData.value.hi_rel_acc || undefined,
      relAccName: inlineEditData.value.hi_rel_acc ? getScodeDisplayName(inlineEditData.value.hi_rel_acc, 'IN') : undefined,
      count: 1,
      minMoney: 0,
      maxMoney: 0,
      scope: '지역'
    });
    
  } catch (error) {
    console.error('키워드 학습 오류:', error);
    alert('키워드 학습 중 오류가 발생했습니다.');
  } finally {
    learningKeyword.value = null;
  }
};

// 모든 미학습 키워드를 한 번에 학습
const learnAllUnlearnedKeywords = async () => {
  if (unlearnedKeywords.value.length === 0) return;
  
  for (const keyword of unlearnedKeywords.value) {
    await learnKeywordToCurrentAccount(keyword);
  }
};

// ============================================
// 키워드 CRUD - 직접 추가
// ============================================

const newKeywordInput = ref('');
const isAddingKeyword = ref(false);

const addCustomKeyword = async () => {
  const keyword = newKeywordInput.value.trim();
  if (!keyword || !inlineEditData.value.hi_scode) return;
  
  // 이미 학습된 키워드인지 확인
  const exists = inlineLearningInfo.value.some(
    item => item.keyword.toLowerCase() === keyword.toLowerCase() && 
            item.scode === inlineEditData.value.hi_scode
  );
  
  if (exists) {
    alert('이미 동일한 키워드가 해당 계정에 학습되어 있습니다.');
    return;
  }
  
  isAddingKeyword.value = true;
  
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    const tableKey = inlineEditData.value.hi_gubun === 'IN' ? 'income' : 'expense';
    
    const response = await axios.post('/api/fna/inout/dictionary/keyword', {
      tableKey,
      sisulCode,
      scode: inlineEditData.value.hi_scode,
      keyword: keyword,
      relAcc: inlineEditData.value.hi_rel_acc || null,
      funding: inlineEditData.value.hi_funding || null
    });
    
    // 성공 시 목록에 추가
    const scodeName = getScodeDisplayName(inlineEditData.value.hi_scode, inlineEditData.value.hi_gubun);
    inlineLearningInfo.value.push({
      idx: response.data.insertId || Date.now(),
      sisulCode: sisulCode || '',
      keyword: keyword,
      scode: inlineEditData.value.hi_scode,
      scodeName: scodeName,
      relAcc: inlineEditData.value.hi_rel_acc || undefined,
      relAccName: inlineEditData.value.hi_rel_acc ? getScodeDisplayName(inlineEditData.value.hi_rel_acc, 'IN') : undefined,
      count: 1,
      minMoney: 0,
      maxMoney: 0,
      scope: '지역'
    });
    
    // 입력 필드 초기화
    newKeywordInput.value = '';
    
  } catch (error) {
    console.error('키워드 추가 오류:', error);
    alert('키워드 추가 중 오류가 발생했습니다.');
  } finally {
    isAddingKeyword.value = false;
  }
};

// ============================================
// 키워드 CRUD - 수정
// ============================================

const editingKeywordIdx = ref<number | null>(null);
const editingKeywordValue = ref('');
const editingKeywordScode = ref('');
const isSavingKeyword = ref(false);

const startKeywordEdit = (item: InlineLearningItem) => {
  editingKeywordIdx.value = item.idx;
  editingKeywordValue.value = item.keyword;
  editingKeywordScode.value = item.scode;
};

const cancelKeywordEdit = () => {
  editingKeywordIdx.value = null;
  editingKeywordValue.value = '';
  editingKeywordScode.value = '';
};

const saveKeywordEdit = async (item: InlineLearningItem) => {
  const newKeyword = editingKeywordValue.value.trim();
  const newScode = editingKeywordScode.value;
  
  if (!newKeyword) {
    alert('키워드를 입력해주세요.');
    return;
  }
  
  // 변경사항 없으면 종료
  if (newKeyword === item.keyword && newScode === item.scode) {
    cancelKeywordEdit();
    return;
  }
  
  isSavingKeyword.value = true;
  
  try {
    const tableKey = inlineEditData.value.hi_gubun === 'IN' ? 'income' : 'expense';
    
    // 키워드만 변경된 경우
    if (newScode === item.scode) {
      await axios.put(`/api/fna/inout/dictionary/keyword/${item.idx}`, {
        tableKey,
        keyword: newKeyword
      });
      
      // 로컬 업데이트
      const idx = inlineLearningInfo.value.findIndex(i => i.idx === item.idx);
      if (idx !== -1) {
        inlineLearningInfo.value[idx].keyword = newKeyword;
      }
    } else {
      // 계정이 변경된 경우: 기존 삭제 후 새로 추가
      await axios.delete(`/api/fna/inout/dictionary/item/${tableKey}/${item.idx}`);
      
      const sisulCode = store.state.user?.m_sisul_code;
      const response = await axios.post('/api/fna/inout/dictionary/keyword', {
        tableKey,
        sisulCode,
        scode: newScode,
        keyword: newKeyword,
        relAcc: inlineEditData.value.hi_rel_acc || null,
        funding: inlineEditData.value.hi_funding || null
      });
      
      // 로컬 업데이트
      const idx = inlineLearningInfo.value.findIndex(i => i.idx === item.idx);
      if (idx !== -1) {
        const scodeName = getScodeDisplayName(newScode, inlineEditData.value.hi_gubun);
        inlineLearningInfo.value[idx] = {
          ...inlineLearningInfo.value[idx],
          idx: response.data.insertId || item.idx,
          keyword: newKeyword,
          scode: newScode,
          scodeName: scodeName
        };
      }
    }
    
    cancelKeywordEdit();
    
  } catch (error) {
    console.error('키워드 수정 오류:', error);
    alert('키워드 수정 중 오류가 발생했습니다.');
  } finally {
    isSavingKeyword.value = false;
  }
};

// 인라인 수정 저장
const saveInlineEdit = async () => {
  if (!editingRowIdx.value || isInlineSaving.value) return;
  
  // 변경사항 확인
  const hasChanges = 
    inlineEditData.value.hi_scode !== originalInlineData.value?.hi_scode ||
    inlineEditData.value.hi_price !== originalInlineData.value?.hi_price ||
    inlineEditData.value.hi_memo !== originalInlineData.value?.hi_memo;
  
  if (!hasChanges) {
    editingRowIdx.value = null;
    return;
  }
  
  isInlineSaving.value = true;
  const savedIdx = editingRowIdx.value;
  
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    
    // 계정 변경 시 학습 수행
    const scodeChanged = inlineEditData.value.hi_scode !== originalInlineData.value?.hi_scode;
    
    // 상대계정 자동 설정 (지출인 경우)
    if (inlineEditData.value.hi_gubun === 'OUT' && !inlineEditData.value.hi_rel_acc) {
      const availableAcc = availableIncomeAccounts.value[0];
      if (availableAcc) {
        inlineEditData.value.hi_rel_acc = availableAcc.scode;
        inlineEditData.value.hi_funding = availableAcc.funding || '06';
      }
    }
    
    // 수입인 경우 자금원천 자동 설정
    if (inlineEditData.value.hi_gubun === 'IN' && inlineEditData.value.hi_scode) {
      const financeCode = financeCodes.value.find(
        fc => fc.fc_scode === inlineEditData.value.hi_scode && fc.fc_trcode === 'IN'
      );
      if (financeCode?.fc_funding) {
        inlineEditData.value.hi_funding = financeCode.fc_funding;
      }
    }
    
    // need_check 상태를 OK로 업데이트
    const updatedNeedCheck = JSON.stringify({
      status: 'OK',
      type: 'inline_edit',
      reason: '인라인 수정 완료',
      modifiedAt: new Date().toISOString()
    });
    
    const payload = {
      ...inlineEditData.value,
      hi_need_check: updatedNeedCheck,
      m_sisul_code: sisulCode
    };
    
    // API 호출
    await axios.put(`/api/fna/inout/${editingRowIdx.value}`, payload);
    
    // 계정 변경 시 학습
    if (scodeChanged && inlineEditData.value.hi_scode) {
      await axios.post('/api/fna/inout/dictionary/learn-single', {
        sisulCode,
        gubun: inlineEditData.value.hi_gubun,
        memo: inlineEditData.value.hi_memo,
        scode: inlineEditData.value.hi_scode,
        funding: inlineEditData.value.hi_funding,
        relAcc: inlineEditData.value.hi_rel_acc,
        price: inlineEditData.value.hi_price
      });
    }
    
    // 로컬 데이터 업데이트
    const localIdx = inoutList.value.findIndex(item => item.hi_idx === savedIdx);
    if (localIdx !== -1) {
      inoutList.value[localIdx] = {
        ...inlineEditData.value,
        hi_need_check: updatedNeedCheck
      };
    }
    
    // 하이라이트 효과 + 토스트 표시
    highlightedIdxList.value = [savedIdx];
    showSaveToast.value = true;
    
    setTimeout(() => {
      highlightedIdxList.value = [];
    }, 2000);
    
    setTimeout(() => {
      showSaveToast.value = false;
    }, 2500);
    
    // 수정 모드 종료
    editingRowIdx.value = null;
    
  } catch (error) {
    console.error('인라인 저장 오류:', error);
    alert('저장 중 오류가 발생했습니다.');
  } finally {
    isInlineSaving.value = false;
  }
};

// ============================================
// 사이드바 위치 토글
// ============================================

const toggleSidebarPosition = () => {
  sidebarPosition.value = sidebarPosition.value === 'left' ? 'right' : 'left';
};

// 월 선택기 열기
const openMonthPicker = () => {
  monthPickerRef.value?.showPicker();
};

// 월 선택기 변경 핸들러
const onMonthPickerChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.value; // "2025-12" 형식
  if (value) {
    const [year, month] = value.split('-');
    currentYear.value = year;
    currentMonth.value = month;
  }
};

// ============================================
// 월 변경 함수
// ============================================

const changeMonth = (delta: number) => {
  let year = parseInt(currentYear.value);
  let month = parseInt(currentMonth.value) + delta;
  
  if (month > 12) {
    month = 1;
    year++;
  } else if (month < 1) {
    month = 12;
    year--;
  }
  
  currentYear.value = year.toString();
  currentMonth.value = month.toString().padStart(2, '0');
};

// ============================================
// API 호출 함수
// ============================================

const fetchFinanceCodes = async () => {
  financeCodesLoading.value = true;
  try {
    const response = await axios.get('/api/finance/codes', {
      params: { year: currentYear.value }
    });
    
    if (response.data.success) {
      financeCodes.value = response.data.data || [];
    }
  } catch (error) {
    console.error('재무코드 조회 오류:', error);
    financeCodes.value = [];
  } finally {
    financeCodesLoading.value = false;
  }
};

const fetchInoutData = async () => {
  isLoading.value = true;
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    if (!sisulCode) {
      console.error('시설 코드가 없습니다.');
      return;
    }
    
    const response = await axios.get('/api/fna/inout', {
      params: {
        sisulCode,
        year: currentYear.value,
        month: currentMonth.value
      }
    });
    
    if (response.data.success) {
      inoutList.value = response.data.data || [];
    }
    
    // 프로세스 상태 조회
    await fetchProcessStatus();
  } catch (error) {
    console.error('현금출납부 조회 오류:', error);
    inoutList.value = [];
  } finally {
    isLoading.value = false;
  }
};

// ============================================
// 프로세스 상태 관리
// ============================================

interface FnaProcessStatus {
  dataYn: boolean;
  confirmedYn: boolean;
  confirmedAt?: string;
  confirmedBy?: string;
}

const processStatus = ref<FnaProcessStatus | null>(null);

// 확정 여부
const isConfirmed = computed(() => processStatus.value?.confirmedYn ?? false);

// 날짜시간 포맷
const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')} ${String(date.getHours()).padStart(2,'0')}:${String(date.getMinutes()).padStart(2,'0')}`;
};

// 프로세스 상태 조회
const fetchProcessStatus = async () => {
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    const yearMonth = `${currentYear.value}${currentMonth.value}`;
    if (!sisulCode || !yearMonth) return;
    
    const response = await axios.get(`/api/fna/process/status/${sisulCode}/${yearMonth}`);
    if (response.data.success) {
      processStatus.value = response.data.data;
    }
  } catch (error) {
    console.error('프로세스 상태 조회 오류:', error);
    processStatus.value = null;
  }
};

// 확정해제
const handleUnconfirm = async () => {
  if (!confirm('확정을 해제하시겠습니까? 해제하면 데이터를 다시 수정할 수 있습니다.')) return;
  
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    
    const yearMonth = `${currentYear.value}${currentMonth.value}`;
    const response = await axios.post(
      `/api/fna/process/unconfirm/${sisulCode}/${yearMonth}`
    );
    
    if (response.data.success) {
      alert('확정이 해제되었습니다.');
      await fetchProcessStatus();
    }
  } catch (error) {
    console.error('확정해제 오류:', error);
    alert('확정 해제 중 오류가 발생했습니다.');
  }
};

// 자동 업로드 완료 핸들러
const handleAutoUploadSaved = async () => {
  await fetchInoutData();
  await refreshProcessStatus(); // 엑셀 업로드 상태 갱신
};

// 프로세스 상태 갱신 (엑셀 업로드 시 호출)
const refreshProcessStatus = async () => {
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    if (!sisulCode) return;
    
    const yearMonth = `${currentYear.value}${currentMonth.value}`;
    const response = await axios.post(`/api/fna/process/refresh/${sisulCode}/${yearMonth}`);
    
    if (response.data.success) {
      processStatus.value = response.data.data;
    }
  } catch (error) {
    console.error('프로세스 상태 갱신 오류:', error);
  }
};

// 누적 이월금 조회
const fetchCarryoverBalance = async () => {
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    if (!sisulCode) return;
    
    const response = await axios.get('/api/fna/inout/carryover', {
      params: {
        sisulCode,
        year: currentYear.value,
        month: currentMonth.value
      }
    });
    
    if (response.data.success) {
      carryoverBalance.value = response.data.data;
    }
  } catch (error) {
    console.error('누적 이월금 조회 오류:', error);
    carryoverBalance.value = { income: 0, expense: 0, balance: 0 };
  }
};

// ============================================
// 다이얼로그 핸들러
// ============================================

const confirmDelete = (item: InoutData) => {
  selectedItem.value = item;
  deleteDialogOpen.value = true;
};

const deleteInout = async () => {
  if (!selectedItem.value?.hi_idx) return;
  
  isDeleting.value = true;
  try {
    await axios.delete(`/api/fna/inout/${selectedItem.value.hi_idx}`);
    deleteDialogOpen.value = false;
    selectedItem.value = null;
    await fetchInoutData();
  } catch (error) {
    console.error('삭제 오류:', error);
  } finally {
    isDeleting.value = false;
  }
};

// 모두 삭제
const deleteAllItems = async () => {
  if (filteredInoutList.value.length === 0) return;
  
  isDeleting.value = true;
  try {
    // 삭제할 항목들의 ID 목록
    const idsToDelete = filteredInoutList.value.map(item => item.hi_idx).filter(id => id !== undefined);
    
    // 병렬로 삭제 요청 (최대 10개씩 배치 처리)
    const batchSize = 10;
    for (let i = 0; i < idsToDelete.length; i += batchSize) {
      const batch = idsToDelete.slice(i, i + batchSize);
      await Promise.all(batch.map(id => axios.delete(`/api/fna/inout/${id}`)));
    }
    
    deleteAllDialogOpen.value = false;
    resetFilters();
    await fetchInoutData();
  } catch (error) {
    console.error('일괄 삭제 오류:', error);
  } finally {
    isDeleting.value = false;
  }
};

// 확정하기 다이얼로그 열기
const openConfirmDialog = () => {
  confirmDialogOpen.value = true;
};

// 확정하기 실행 (상대계정 자동채우기 + 확정)
const executeConfirm = async () => {
  isConfirming.value = true;
  const updatedIdxList: number[] = [];
  
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    const userName = store.state.user?.m_name || '시스템';
    
    // 1. 상대계정 없는 항목이 있으면 먼저 자동 채우기
    if (missingRelAccCount.value > 0 && availableIncomeAccounts.value.length > 0) {
      isFillingRelAcc.value = true;
      
      // 잔액 복사본 생성 (로컬에서 차감 관리)
      const balanceMap: Record<string, number> = {};
      const fundingMap: Record<string, string | undefined> = {};
      availableIncomeAccounts.value.forEach(acc => {
        balanceMap[acc.scode] = acc.availableBalance;
        fundingMap[acc.scode] = acc.funding;
      });
      
      // 상대계정 없는 지출 항목들 (hi_idx 순)
      const itemsToUpdate = [...missingRelAccItems.value];
      
      for (const item of itemsToUpdate) {
        const expenseAmount = item.hi_price || 0;
        
        // 잔액이 있는 수입 계정 찾기
        let selectedScode: string | null = null;
        
        // 먼저 금액이 충분한 계정 찾기
        for (const acc of availableIncomeAccounts.value) {
          if (balanceMap[acc.scode] >= expenseAmount) {
            selectedScode = acc.scode;
            break;
          }
        }
        
        // 충분한 계정이 없으면 잔액이 있는 첫 번째 계정 사용
        if (!selectedScode) {
          for (const acc of availableIncomeAccounts.value) {
            if (balanceMap[acc.scode] > 0) {
              selectedScode = acc.scode;
              break;
            }
          }
        }
        
        if (!selectedScode) {
          console.warn(`[확정] 잔액 부족으로 ${item.hi_idx} 항목 건너뜀`);
          continue;
        }
        
        const selectedFunding = fundingMap[selectedScode] || item.hi_funding;
        const updatedNeedCheck = JSON.stringify({ status: 'OK', message: '확정 시 자동 채우기' });
        
        try {
          await axios.put(`/api/fna/inout/${item.hi_idx}`, {
            ...item,
            hi_rel_acc: selectedScode,
            hi_funding: selectedFunding,
            hi_need_check: updatedNeedCheck,
            m_sisul_code: sisulCode
          });
          
          balanceMap[selectedScode] -= expenseAmount;
          
          const localIdx = inoutList.value.findIndex(i => i.hi_idx === item.hi_idx);
          if (localIdx !== -1) {
            inoutList.value[localIdx].hi_rel_acc = selectedScode;
            inoutList.value[localIdx].hi_funding = selectedFunding;
            inoutList.value[localIdx].hi_need_check = updatedNeedCheck;
          }
          
          updatedIdxList.push(item.hi_idx!);
        } catch (err) {
          console.error(`[확정] ${item.hi_idx} 업데이트 실패:`, err);
        }
      }
      
      isFillingRelAcc.value = false;
    }
    
    // 2. 확정 처리
    const yearMonth = `${currentYear.value}${currentMonth.value}`;
    const response = await axios.post(
      `/api/fna/process/confirm/${sisulCode}/${yearMonth}`,
      { userName }
    );
    
    if (response.data.success) {
      // 하이라이트 효과
      if (updatedIdxList.length > 0) {
        highlightedIdxList.value = updatedIdxList;
        setTimeout(() => {
          highlightedIdxList.value = [];
        }, 2000);
      }
      
      confirmDialogOpen.value = false;
      await fetchProcessStatus();
      
      if (updatedIdxList.length > 0) {
        alert(`${updatedIdxList.length}건의 상대계정을 채우고 확정되었습니다.`);
      } else {
        alert('확정되었습니다.');
      }
    }
    
  } catch (error) {
    console.error('확정 처리 오류:', error);
    alert('확정 처리 중 오류가 발생했습니다.');
  } finally {
    isConfirming.value = false;
    isFillingRelAcc.value = false;
  }
};

// ============================================
// 학습관리 기능
// ============================================

const getNeedCheckClass = (needCheck: string | undefined): string => {
  if (!needCheck) return '';
  try {
    const info = JSON.parse(needCheck);
    if (!info.prediction) return 'error';
    if (info.reason?.includes('낮은')) return 'warning';
    return 'info';
  } catch {
    return 'error';
  }
};

// 실시간 상대계정 분포 조회
const realTimeRelAccDistribution = ref<Array<{rel_acc: string; sname: string; count: number; percentage: number}>>([]);

const openLearningModal = async (item: InoutData) => {
  learningItem.value = item;
  learningFormData.value.scode = item.hi_scode || '';
  learningFormData.value.funding = item.hi_funding || '';
  learningFormData.value.relAcc = item.hi_rel_acc || '';
  realTimeRelAccDistribution.value = [];
  learningModalOpen.value = true;
  
  // 지출 항목이고 계정코드가 있으면 실시간으로 상대계정 분포 조회
  if (item.hi_gubun === 'OUT' && item.hi_scode) {
    try {
      const response = await axios.get(`/api/fna/inout/dictionary/rel-acc-distribution/${item.hi_scode}`);
      if (response.data.success && response.data.distribution) {
        realTimeRelAccDistribution.value = response.data.distribution;
      }
    } catch (err) {
      console.warn('[RelAccDist] 조회 실패:', err);
    }
  }
};

// 학습 모달에서 계정 변경 시 상대계정 분포 다시 조회
const onLearningScodeChange = async (newScode: string) => {
  if (!learningItem.value) return;
  
  // 지출 항목이고 계정코드가 있으면 실시간으로 상대계정 분포 조회
  if (learningItem.value.hi_gubun === 'OUT' && newScode) {
    try {
      realTimeRelAccDistribution.value = [];
      const response = await axios.get(`/api/fna/inout/dictionary/rel-acc-distribution/${newScode}`);
      if (response.data.success && response.data.distribution) {
        realTimeRelAccDistribution.value = response.data.distribution;
      }
    } catch (err) {
      console.warn('[RelAccDist] 조회 실패:', err);
    }
  } else {
    realTimeRelAccDistribution.value = [];
  }
};

const getReasonClass = (reason: string): string => {
  if (reason?.includes('실패') || reason?.includes('없음')) return 'error';
  if (reason?.includes('낮은')) return 'warning';
  return 'info';
};

const getReasonIcon = (reason: string): string => {
  if (reason?.includes('실패') || reason?.includes('없음')) return 'mdi-alert-circle';
  if (reason?.includes('낮은')) return 'mdi-alert';
  return 'mdi-information';
};

const getProbabilityClass = (probability: number): string => {
  if (probability >= 80) return 'high';
  if (probability >= 50) return 'medium';
  return 'low';
};

// 상대계정 추론 근거 간소화 (회계 실무자 관점)
const getSimplifiedRelAccReason = (reason: string): string => {
  if (!reason) return '';
  if (reason.includes('학습된 매핑')) {
    const match = reason.match(/→ "([^"]+)"/);
    return match ? `과거 거래에서 "${match[1]}"과 자주 연결됨` : '과거 거래 패턴 기반';
  }
  if (reason.includes('잔액 충분')) return '해당 수입 계정에 잔액 있음';
  if (reason.includes('잔액 부족')) return '잔액이 충분한 수입 계정 선택';
  return reason.replace(/"/g, '').slice(0, 50);
};

// 추론 사유 간소화
const getSimplifiedReason = (reason: string): string => {
  if (!reason) return '';
  if (reason.includes('신뢰도')) return '';
  if (reason.includes('매칭')) return '';
  if (reason.includes('범위')) return '금액이 일반적인 범위와 다름';
  return reason.slice(0, 50);
};

// 사용 가능한 상대계정 목록 (잔액 있는 수입 계정 + 현재 선택된 계정)
const availableRelAccList = computed(() => {
  // 현재 선택된 항목 이전의 수입 항목들 중 잔액이 있는 것
  const currentIdx = learningItem.value?.hi_idx || 0;
  const currentRelAcc = learningItem.value?.hi_rel_acc;
  
  const incomeItems = inoutList.value.filter((item: InoutData) => 
    item.hi_gubun === 'IN' && 
    item.hi_scode && 
    (item.hi_idx || 0) < currentIdx
  );
  
  // scode별로 잔액 계산
  const balanceMap: Record<string, { scode: string; sname: string; balance: number }> = {};
  
  incomeItems.forEach((item: InoutData) => {
    const scode = item.hi_scode;
    if (!scode) return;
    
    if (!balanceMap[scode]) {
      balanceMap[scode] = {
        scode,
        sname: getScodeDisplayName(scode, 'IN'),
        balance: 0
      };
    }
    balanceMap[scode].balance += (item.hi_price || 0);
  });
  
  // 지출로 차감 (현재 항목 제외)
  const expenseItems = inoutList.value.filter((item: InoutData) => 
    item.hi_gubun === 'OUT' && 
    item.hi_rel_acc &&
    (item.hi_idx || 0) < currentIdx &&
    item.hi_idx !== learningItem.value?.hi_idx
  );
  
  expenseItems.forEach((item: InoutData) => {
    const relAcc = item.hi_rel_acc;
    if (relAcc && balanceMap[relAcc]) {
      balanceMap[relAcc].balance -= (item.hi_price || 0);
    }
  });
  
  // 결과 배열 생성
  let result = Object.values(balanceMap)
    .filter(item => item.balance > 0)
    .map(item => ({
      ...item,
      displayName: item.sname
    }))
    .sort((a, b) => b.balance - a.balance);
  
  // 현재 선택된 상대계정이 목록에 없으면 추가
  if (currentRelAcc && !result.find(r => r.scode === currentRelAcc)) {
    const sname = getScodeDisplayName(currentRelAcc, 'IN');
    result.unshift({
      scode: currentRelAcc,
      sname: sname,
      balance: 0,
      displayName: sname
    });
  }
  
  return result;
});

const getMoneyMatchClass = (moneyMatch: number | undefined): string => {
  if (moneyMatch === 2) return 'high';
  if (moneyMatch === 1) return 'medium';
  return 'low';
};

const getMoneyMatchText = (moneyMatch: number | undefined): string => {
  if (moneyMatch === 2) return '정확히 일치';
  if (moneyMatch === 1) return '범위 내';
  return '범위 외';
};

// 금액 범위 시각화 함수
const getRangeBarStyle = (minMoney: number, maxMoney: number, inputPrice: number) => {
  // 전체 범위 계산 (입력값 포함해서)
  const allValues = [minMoney, maxMoney, inputPrice];
  const min = Math.min(...allValues) * 0.8;
  const max = Math.max(...allValues) * 1.2;
  const range = max - min;
  
  if (range === 0) return { left: '10%', width: '80%' };
  
  const left = ((minMoney - min) / range) * 100;
  const width = ((maxMoney - minMoney) / range) * 100;
  
  return {
    left: `${Math.max(5, Math.min(90, left))}%`,
    width: `${Math.max(10, Math.min(85, width))}%`
  };
};

const getInputPriceMarkerStyle = (minMoney: number, maxMoney: number, inputPrice: number) => {
  const allValues = [minMoney, maxMoney, inputPrice];
  const min = Math.min(...allValues) * 0.8;
  const max = Math.max(...allValues) * 1.2;
  const range = max - min;
  
  if (range === 0) return { left: '50%' };
  
  const left = ((inputPrice - min) / range) * 100;
  return { left: `${Math.max(2, Math.min(98, left))}%` };
};

// 분포 위치 계산 (0~100%)
const getDistributionPosition = (minMoney: number, maxMoney: number, value: number): number => {
  if (maxMoney <= minMoney) return 50;
  const position = ((value - minMoney) / (maxMoney - minMoney)) * 100;
  return Math.max(0, Math.min(100, Math.round(position)));
};

// 범위 조정 모달
const rangeAdjustModalOpen = ref(false);
const rangeAdjustData = ref({
  scode: '',
  scodeName: '',
  keyword: '',
  currentMin: 0,
  currentMax: 0,
  newMin: 0,
  newMax: 0,
  gubun: 'IN' as 'IN' | 'OUT'
});

const openRangeAdjustModal = async () => {
  if (!learningItem.value || !parsedNeedCheck.value?.prediction) return;
  
  const prediction = parsedNeedCheck.value.prediction;
  const gubun = learningItem.value.hi_gubun;
  const scode = prediction.scode || learningItem.value.hi_scode || '';
  
  // 현재 범위 설정
  rangeAdjustData.value = {
    scode: scode,
    scodeName: getScodeDisplayName(scode, gubun),
    keyword: parsedNeedCheck.value.keywords?.join(', ') || '',
    currentMin: prediction.minMoney || 0,
    currentMax: prediction.maxMoney || 0,
    newMin: Math.min(prediction.minMoney || 0, learningItem.value.hi_price),
    newMax: Math.max(prediction.maxMoney || 0, learningItem.value.hi_price),
    gubun: gubun as 'IN' | 'OUT'
  };
  
  rangeAdjustModalOpen.value = true;
};

const saveRangeAdjust = async () => {
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    const response = await axios.post('/api/fna/inout/dictionary/adjust-range', {
      sisul_code: sisulCode,
      gubun: rangeAdjustData.value.gubun,
      scode: rangeAdjustData.value.scode,
      keywords: parsedNeedCheck.value?.keywords || [],
      min_money: rangeAdjustData.value.newMin,
      max_money: rangeAdjustData.value.newMax
    });
    
    if (response.data.success) {
      alert('금액 범위가 조정되었습니다.');
      rangeAdjustModalOpen.value = false;
    } else {
      alert('범위 조정 실패: ' + response.data.message);
    }
  } catch (error: any) {
    console.error('범위 조정 오류:', error);
    alert('범위 조정 중 오류가 발생했습니다.');
  }
};

const formatSource = (source: string): string => {
  const sourceMap: Record<string, string> = {
    'dictionary': '시설 딕셔너리',
    'dictionary-global': '전역 딕셔너리',
    'dictionary+relmap': '시설 딕셔너리 + 매핑',
    'dictionary-global+relmap': '전역 딕셔너리 + 매핑'
  };
  return sourceMap[source] || source || '-';
};

const getScodeOptions = (gubun: string) => {
  if (gubun === 'IN') {
    return incomeCodeList.value;
  } else {
    return expenseCodeList.value;
  }
};

const applyAlternative = (alt: { scode: string; rel_acc?: string; funding?: string }) => {
  learningFormData.value.scode = alt.scode;
  if (alt.funding) {
    learningFormData.value.funding = alt.funding;
  }
  if (alt.rel_acc) {
    learningFormData.value.relAcc = alt.rel_acc;
  }
};

// 유사 항목 개수 확인 후 저장
const checkSimilarAndSave = async () => {
  // 인라인 편집과 폼 데이터 모두 확인
  const hasScode = learningItem.value?.hi_scode || learningFormData.value.scode;
  
  if (!learningItem.value?.hi_idx || !hasScode) {
    // 계정코드가 없으면 바로 저장 (유사 항목 적용 안함)
    await saveLearningData(false);
    return;
  }
  
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    
    // 유사 항목 개수 조회
    const countResult = await axios.post('/api/fna/inout/dictionary/count-similar', {
      sisulCode,
      gubun: learningItem.value.hi_gubun,
      memo: learningItem.value.hi_memo,
      excludeIdx: learningItem.value.hi_idx
    });
    
    if (countResult.data.count > 0) {
      // 유사 항목이 있으면 확인 모달 띄우기
      similarItemsCount.value = countResult.data.count;
      applySimilarConfirmOpen.value = true;
    } else {
      // 유사 항목이 없으면 바로 저장
      await saveLearningData(false);
    }
  } catch (error) {
    console.error('유사 항목 확인 오류:', error);
    // 오류 발생 시 바로 저장 진행
    await saveLearningData(false);
  }
};

const saveLearningData = async (applyToAll: boolean) => {
  if (!learningItem.value?.hi_idx) return;
  
  // 확인 모달 닫기
  applySimilarConfirmOpen.value = false;
  
  isSavingLearning.value = true;
  try {
    const sisulCode = store.state.user?.m_sisul_code;
    
    // 사용자가 수정 완료했으므로 status를 OK로 설정
    const updatedNeedCheck = JSON.stringify({
      status: 'OK',
      type: 'manual',
      reason: '사용자 수정 완료 및 학습',
      modifiedAt: new Date().toISOString()
    });
    
    // learningItem에 직접 바인딩된 값 사용 (인라인 편집 지원)
    const finalScode = learningItem.value.hi_scode || learningFormData.value.scode || null;
    const finalFunding = learningItem.value.hi_funding || learningFormData.value.funding || null;
    const finalRelAcc = learningItem.value.hi_rel_acc || learningFormData.value.relAcc || null;
    
    const payload = {
      ...learningItem.value,
      hi_scode: finalScode,
      hi_funding: finalFunding,
      hi_rel_acc: finalRelAcc,
      hi_need_check: updatedNeedCheck,
      m_sisul_code: sisulCode
    };
    
    // 1. 항목 업데이트
    await axios.put(`/api/fna/inout/${learningItem.value.hi_idx}`, payload);
    
    // 로컬 데이터 업데이트 (스크롤 유지)
    const updatedIdxList: number[] = [learningItem.value.hi_idx];
    
    // 현재 항목 로컬 업데이트
    const currentIdx = inoutList.value.findIndex(item => item.hi_idx === learningItem.value?.hi_idx);
    if (currentIdx !== -1) {
      inoutList.value[currentIdx] = {
        ...inoutList.value[currentIdx],
        hi_scode: finalScode || undefined,
        hi_funding: finalFunding || undefined,
        hi_rel_acc: finalRelAcc || undefined,
        hi_need_check: JSON.stringify({
          status: 'OK',
          type: 'manual',
          reason: '사용자 수정 완료 및 학습',
          modifiedAt: new Date().toISOString()
        })
      };
    }
    
    // 2. 명시적 학습 호출 (계정코드가 있는 경우만)
    // finalScode를 사용하여 인라인 편집과 폼 데이터 모두 지원
    if (finalScode) {
      await axios.post('/api/fna/inout/dictionary/learn-single', {
        sisulCode,
        gubun: learningItem.value.hi_gubun,
        memo: learningItem.value.hi_memo,
        scode: finalScode,
        funding: finalFunding,
        relAcc: finalRelAcc,
        price: learningItem.value.hi_price
      });
      
      // 3. 유사 항목 적용 (사용자가 선택한 경우에만)
      if (applyToAll) {
        const applySimilarResult = await axios.post('/api/fna/inout/dictionary/apply-similar', {
          sisulCode,
          gubun: learningItem.value.hi_gubun,
          memo: learningItem.value.hi_memo,
          scode: finalScode,
          funding: finalFunding,
          relAcc: finalRelAcc,
          excludeIdx: learningItem.value.hi_idx  // 현재 항목 제외
        });
        
        if (applySimilarResult.data.updatedCount > 0) {
          // 업데이트된 항목들의 idx 목록을 받아서 로컬에서도 업데이트
          const updatedItems: number[] = applySimilarResult.data.updatedItems || [];
          for (const updatedIdx of updatedItems) {
            updatedIdxList.push(updatedIdx);
            const idx = inoutList.value.findIndex(item => item.hi_idx === updatedIdx);
            if (idx !== -1) {
              inoutList.value[idx] = {
                ...inoutList.value[idx],
                hi_scode: finalScode || undefined,
                hi_funding: finalFunding || undefined,
                hi_rel_acc: finalRelAcc || undefined,
                hi_need_check: JSON.stringify({
                  status: 'OK',
                  type: 'auto_applied',
                  reason: '학습 내용 자동 적용',
                  appliedAt: new Date().toISOString()
                })
              };
            }
          }
        }
      }
    }
    
    // 하이라이트 효과
    highlightedIdxList.value = updatedIdxList;
    setTimeout(() => {
      highlightedIdxList.value = [];
    }, 2000);
    
    learningModalOpen.value = false;
    learningItem.value = null;
  } catch (error) {
    console.error('학습 저장 오류:', error);
    alert('저장 또는 학습 중 오류가 발생했습니다.');
  } finally {
    isSavingLearning.value = false;
  }
};

// ============================================
// 워처 및 초기화
// ============================================

// 연도/월 변경 시 데이터 다시 조회
watch([currentYear, currentMonth], () => {
  fetchInoutData();
  fetchCarryoverBalance();
  fetchFinanceCodes();
});

// 학습 모달에서 계정 선택 시 자금원천 자동 설정
watch(() => learningFormData.value.scode, (newScode) => {
  if (!newScode || !learningItem.value) return;
  
  // 수입의 경우: financeCodes에서 해당 계정의 funding 가져오기
  if (learningItem.value.hi_gubun === 'IN') {
    const financeCode = financeCodes.value.find(fc => fc.fc_scode === newScode && fc.fc_trcode === 'IN');
    if (financeCode?.fc_funding) {
      learningFormData.value.funding = financeCode.fc_funding;
    }
  }
});

// 지출의 경우: 상대계정(수입 계정) 선택 시 해당 계정의 funding 가져오기
watch(() => learningFormData.value.relAcc, (newRelAcc) => {
  if (!newRelAcc || !learningItem.value) return;
  
  // 지출의 경우: 상대계정(수입 계정)의 fc_funding을 자금원천으로 사용
  if (learningItem.value.hi_gubun === 'OUT') {
    const financeCode = financeCodes.value.find(fc => fc.fc_scode === newRelAcc && fc.fc_trcode === 'IN');
    if (financeCode?.fc_funding) {
      learningFormData.value.funding = financeCode.fc_funding;
    }
  }
});

onMounted(async () => {
  await Promise.all([
    fetchFinanceCodes(),
    fetchInoutData(),
    fetchCarryoverBalance()
  ]);
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/pgmInout.scss";
</style>


